import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MapPin, List as ListIcon, Map as MapIcon, RefreshCw, AlertTriangle, X, ExternalLink, Layers, Crosshair } from 'lucide-react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { AuthContext } from '../../contexts/AuthContext';
import { fetchWithTimeout } from '../../utils/network.utils';
import { Portal } from '../Portal';

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN || 'pk.eyJ1IjoiY2xpZmZpc29uIiwiYSI6ImNtNHZqZ3p1cTA3MmcycHE2bDdtdmlwdmYifQ.6kIxcCZXH6I9gHe41FhkMw';
mapboxgl.accessToken = MAPBOX_TOKEN;

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080';

interface FleetMachine {
  equipment_id: number;
  equipment_name: string;
  bradanna_equipment_number: string | null;
  equipment_type: string;
  // Optional secondary tag (asphalt/farm) — drives the By Type sub-type cards
  // and the By Job sub-grouping. Null/undefined = untagged.
  equipment_subtype?: string | null;
  status: string;
  traccar_device_id: number | null;
  lat: number | null;
  lng: number | null;
  ignition_on: boolean;
  current_hours: number | null;
  synced_at: string | null;
  // Honest tracker-health signals (see backend tracker-health.service.js). Unlike
  // synced_at — which the 3 AM cron re-stamps from stale positions so a dead
  // tracker looks "fresh" — last_fix_at only advances on a VALID GPS fix, and
  // power_v is the machine supply voltage (a dead battery is the #1 cause of a
  // tracker going dark). The dead-tracker badge reads these, not synced_at.
  last_fix_at?: string | null;
  gps_valid?: boolean | null;
  power_v?: number | null;            // latest supply voltage (display)
  resting_power_v?: number | null;    // engine-off resting avg — crank-proof; badge threshold uses this
  no_fix?: boolean;                   // server-computed: running a while with zero valid GPS fixes
  battery_v?: number | null;
  ran_today_minutes: number;
  ran_yesterday_minutes: number;
  is_idle: boolean;
  idle_days?: number | null;     // days since last ignition_on; 0 = recently active
  last_ran_at?: string | null;
  current_speed_kph?: number | null;
  // Server-computed location classification (docs/FLEET-TRANSIT-STATE.md):
  // in_transit | unloading | at_job | off_site.
  transit_state?: 'in_transit' | 'unloading' | 'at_job' | 'off_site';
  hauler_id?: number | null;
  // Highest-severity open issue (critical/moderate) recorded on the machine.
  open_issue?: { issue_id: number; severity: string; issue_description: string; reported_date?: string | null } | null;
  // Engine hours at the service baseline (null = no inspection on record).
  last_inspection_hours?: number | null;
  // What the baseline is: a real service, a plain inspection, the factory (machine
  // was new when tracked), or 'none' (used machine, never inspected → NO RECORD badge).
  service_baseline?: 'service' | 'inspection' | 'factory' | 'none';
  // Rental-only fields
  is_rental?: boolean;
  rental_id?: number;
  rental_company?: string;
  days_on_rent?: number;
}

interface RentalRow {
  rental_id: number;
  equipment_type: string;
  description: string | null;
  rental_company: string;
  vendor_equipment_id: string | null;
  job_id: number | null;
  job_name: string | null;
  bradanna_job_number: string | null;
  start_date: string;
  notes: string | null;
  days_on_rent: number;
}

// Day count for idle machines ("14d") — shown as muted text in the row's
// run-hours slot. No cap, so truly forgotten equipment is obvious. Returns
// null for non-idle machines.
const unusedLabel = (m: FleetMachine): string | null => {
  if (!m.is_idle) return null;
  const d = m.idle_days;
  if (d == null) return '2d+';
  if (d < 2) return '2d';
  return `${d}d`;
};

// Spread machines that are physically close to each other so their labels
// don't overlap on the map. Uses DISTANCE-BASED grouping (any pair within
// GROUP_RADIUS_METERS clusters together) — a fixed-grid would leave two
// machines straddling a cell boundary unspread. Offset distance is computed
// in pixels at the current map zoom so the visual separation stays consistent.
// Deterministic by equipment_id so SSE updates animate smoothly.
//
// Tuning:
//   GROUP_RADIUS_METERS — how close two machines must be to be considered
//     "stacked". 80m catches typical shop bay + adjacent parking.
//   PX_CENTER_TO_CENTER_BASE — pixels between adjacent marker centers in a
//     2-machine group. Labels are ~120-150px wide so this needs to be ≥140
//     for labels to actually separate.
const GROUP_RADIUS_METERS = 80;
// Center-to-center pixels between adjacent markers in a 2-marker group.
// Markers are stacked NORTH/SOUTH (see angle rotation below), so labels at
// different y-coords don't fight even at this smaller distance.
const PX_CENTER_TO_CENTER_BASE = 34;
const spreadOverlaps = <T extends { equipment_id: number; lat: number; lng: number }>(
  machines: T[],
  zoom: number,
): (T & { displayLat: number; displayLng: number; labelLeft: boolean })[] => {
  const valid = machines.filter(m => Number.isFinite(m.lat) && Number.isFinite(m.lng));
  const distMeters = (a: T, b: T) => {
    const R = 6_371_000;
    const dLat = (b.lat - a.lat) * Math.PI / 180;
    const dLng = (b.lng - a.lng) * Math.PI / 180;
    const la1 = a.lat * Math.PI / 180;
    const la2 = b.lat * Math.PI / 180;
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
  };
  const groups: T[][] = [];
  const assigned = new Set<number>();
  for (let i = 0; i < valid.length; i++) {
    if (assigned.has(i)) continue;
    const group: T[] = [valid[i]];
    assigned.add(i);
    for (let j = i + 1; j < valid.length; j++) {
      if (assigned.has(j)) continue;
      if (distMeters(valid[i], valid[j]) < GROUP_RADIUS_METERS) {
        group.push(valid[j]);
        assigned.add(j);
      }
    }
    groups.push(group);
  }
  const result: (T & { displayLat: number; displayLng: number; labelLeft: boolean })[] = [];
  for (const group of groups) {
    if (group.length === 1) {
      const m = group[0];
      result.push({ ...m, displayLat: m.lat, displayLng: m.lng, labelLeft: false });
    } else {
      const sorted = [...group].sort((a, b) => a.equipment_id - b.equipment_id);
      const lat = sorted[0].lat;
      // Fan around the group CENTROID (mean of all members), not each machine's own
      // GPS — otherwise the ring is a scatter, not a circle.
      const cLat = sorted.reduce((s, m) => s + m.lat, 0) / sorted.length;
      const cLng = sorted.reduce((s, m) => s + m.lng, 0) / sorted.length;
      // Clamp the zoom used for the offset to the label-visible floor (14).
      // Per-machine markers are hidden below 14 (clusters take over), so the fan
      // only ever shows at >=14. If this is called with a STALE low zoom (e.g. a
      // hidden-map fitBounds left zoom ~7), an unclamped offset throws the fanned
      // marker 100+ miles with a giant leader line. Clamping caps the worst case
      // to the zoom-14 fan; at normal zoom (>=14) it's unchanged.
      const effZoom = Math.max(zoom, 14);
      const metersPerPx = 156543.03 * Math.cos(lat * Math.PI / 180) / Math.pow(2, effZoom);
      // Ring radius in pixels. For 2 machines, center-to-center = base.
      // Grows with group size (log2) so 8 machines fan further than 2.
      const pxRadius = (PX_CENTER_TO_CENTER_BASE / 2) * Math.max(1, Math.log2(sorted.length + 1));
      const metersRadius = pxRadius * metersPerPx;
      const degRadius = metersRadius / 111_000;
      // Rotate so the first marker sits NORTH of center. For a 2-marker
      // group this puts one above and one below the actual GPS point —
      // labels are at different y-coords and don't crash into each other.
      const cosLat = Math.max(0.1, Math.cos(lat * Math.PI / 180));
      for (let i = 0; i < sorted.length; i++) {
        // Half-step rotation (+PI/n) so no machine sits dead-top — leaves a gap for
        // the job-name label. labelLeft = west half → label hangs outward to the left.
        const angle = (i / sorted.length) * 2 * Math.PI + Math.PI / 2 + Math.PI / sorted.length;
        result.push({
          ...sorted[i],
          displayLat: cLat + degRadius * Math.sin(angle),
          displayLng: cLng + degRadius * Math.cos(angle) / cosLat,
          labelLeft: Math.cos(angle) < 0,
        });
      }
    }
  }
  return result;
};

// Build a GeoJSON FeatureCollection of "true position" indicators for machines
// whose marker has been offset by spreadOverlaps. For each offset machine we
// emit a leader LINE (offset marker dot -> real GPS point) and a DOT at the real
// GPS point, so the exact ground position is always visible even though the
// label is fanned out for readability. Solo (non-offset) machines are skipped —
// their HTML marker dot already sits on the true position.
const buildTrueposFC = (
  spread: { lat: number | null; lng: number | null; displayLat: number; displayLng: number }[]
) => ({
  type: 'FeatureCollection' as const,
  features: spread.flatMap(m => {
    const { lat, lng, displayLat, displayLng } = m;
    if (lat == null || lng == null || (displayLat === lat && displayLng === lng)) return [];
    return [
      { type: 'Feature' as const, properties: { kind: 'line' }, geometry: { type: 'LineString' as const, coordinates: [[displayLng, displayLat], [lng, lat]] } },
      { type: 'Feature' as const, properties: { kind: 'dot' }, geometry: { type: 'Point' as const, coordinates: [lng, lat] } },
    ];
  }),
});

interface FleetGroup {
  job_id: number | null;
  job_name: string;
  bradanna_job_number: string | null;
  equipment: FleetMachine[];
  // Foreman(s) currently clocked into this job (comma-separated), from the
  // server snapshot. Null when nobody's clocked in.
  foreman_names?: string | null;
  crew_count?: number;
}

interface Hauler {
  equipment_id: number;
  equipment_name: string;
  bradanna_equipment_number: string | null;
  lat: number | null;
  lng: number | null;
  current_speed_kph?: number | null;
  moving?: boolean;
  ran_today_minutes?: number;
  synced_at?: string | null;
}

interface FleetData {
  groups: FleetGroup[];
  haulers?: Hauler[];
  totals: { tracked_count: number; running_count: number; idle_count: number };
}

interface PwaTrailer {
  trailer_id: string;
  machines: FleetMachine[];
  lat: number;
  lng: number;
  speed_kph: number;
  haulerId: number | null;
  haulerName: string | null;
  synced_at: string | null;   // fix time of the truck's latest position (for smooth playback)
}

// Group server-flagged in_transit machines into trailers for the map: by
// hauler_id when a tracked hauler is present (pinned to the truck), else by
// proximity. 200m radius so two trackers on ONE trailer don't split apart from
// GPS jitter as the rig moves. Returns trailers + the ids absorbed (pulled from
// the per-machine marker pass). Mirrors bfav5's clusterTrailers.
const TRANSIT_GROUP_RADIUS_M = 200;
function clusterTransit(machines: FleetMachine[], haulers: Hauler[], sticky?: Map<number, number>): { trailers: PwaTrailer[]; absorbedIds: Set<number> } {
  const absorbedIds = new Set<number>();
  const trailers: PwaTrailer[] = [];
  const dist = (aLat: number, aLng: number, bLat: number, bLng: number) => {
    const R = 6_371_000, p = Math.PI / 180;
    const dLa = (bLat - aLat) * p, dLo = (bLng - aLng) * p, la1 = aLat * p, la2 = bLat * p;
    const h = Math.sin(dLa / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dLo / 2) ** 2;
    return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
  };

  // 1) Hauler-anchored
  const byHauler = new Map<number, FleetMachine[]>();
  for (const m of machines) {
    if (m.transit_state === 'in_transit' && m.hauler_id != null && !m.is_rental) {
      if (!byHauler.has(m.hauler_id)) byHauler.set(m.hauler_id, []);
      byHauler.get(m.hauler_id)!.push(m);
    }
  }
  for (const h of haulers) {
    if (h.lat == null || h.lng == null) continue;
    const members = (byHauler.get(h.equipment_id) || []).sort((a, b) => a.equipment_id - b.equipment_id);
    if (members.length === 0) continue;
    members.forEach(m => absorbedIds.add(m.equipment_id));
    trailers.push({ trailer_id: `hauler-${h.equipment_id}`, machines: members, lat: h.lat, lng: h.lng, speed_kph: h.current_speed_kph ?? 0, haulerId: h.equipment_id, haulerName: h.equipment_name, synced_at: h.synced_at ?? null });
  }

  const transit = machines.filter(m => m.transit_state === 'in_transit' && m.hauler_id == null && m.lat != null && m.lng != null && !m.is_rental && !absorbedIds.has(m.equipment_id));

  // 2a) Absorb a loose in_transit machine into a NEARBY hauler trailer first.
  //     The server can anchor one trailer-mate to the truck (hauler_id set) but
  //     miss another (tracker lag → that machine's fix sits off the truck's
  //     trail at compute time → hauler_id null). Without this, that mate falls
  //     through to its own sticky group and pops out as a SECOND in-transit
  //     marker. If a loose in_transit machine is within trailer range of a
  //     hauler trailer, it's physically on that truck — merge it in.
  const haulerTrailers = trailers.filter(t => t.haulerId != null);
  const loose: FleetMachine[] = [];
  for (const m of transit) {
    let best: PwaTrailer | null = null, bestD = Infinity;
    for (const t of haulerTrailers) {
      const d = dist(Number(m.lat), Number(m.lng), t.lat, t.lng);
      if (d < TRANSIT_GROUP_RADIUS_M && d < bestD) { best = t; bestD = d; }
    }
    if (best) { best.machines.push(m); absorbedIds.add(m.equipment_id); }
    else loose.push(m);
  }
  // Keep each trailer's machine list ordered after absorbing (label lists each
  // machine on its own line, so a stable order avoids flicker).
  haulerTrailers.forEach(t => t.machines.sort((a, b) => a.equipment_id - b.equipment_id));

  // 2) Untracked: STICKY grouping. Machines that go in_transit together (co-
  //    located on first appearance) share a stable groupId and KEEP it for the
  //    whole trip — no distance check — so report-lag at speed can't split them.
  //    A machine leaves only when it stops being in_transit (engine started =
  //    unloaded, or parked). Marker uses the latest-reporting member's position.
  const sm = sticky ?? new Map<number, number>();
  const liveIds = new Set(loose.map(m => m.equipment_id));
  for (const id of [...sm.keys()]) if (!liveIds.has(id)) sm.delete(id); // left transit / absorbed onto a hauler → ungroup
  for (const m of loose) {
    if (sm.has(m.equipment_id)) continue;
    let gid = m.equipment_id;
    for (const other of loose) {
      if (other.equipment_id !== m.equipment_id && sm.has(other.equipment_id)
          && dist(Number(m.lat), Number(m.lng), Number(other.lat), Number(other.lng)) < TRANSIT_GROUP_RADIUS_M) { gid = sm.get(other.equipment_id)!; break; }
    }
    sm.set(m.equipment_id, gid);
  }
  const byGroup = new Map<number, FleetMachine[]>();
  for (const m of loose) {
    const g = sm.get(m.equipment_id)!;
    if (!byGroup.has(g)) byGroup.set(g, []);
    byGroup.get(g)!.push(m);
  }
  for (const [g, members] of byGroup) {
    const sorted = members.sort((a, b) => a.equipment_id - b.equipment_id);
    sorted.forEach(m => absorbedIds.add(m.equipment_id));
    const newest = sorted.reduce((p, c) => (new Date(c.synced_at || 0).getTime() > new Date(p.synced_at || 0).getTime() ? c : p), sorted[0]);
    trailers.push({ trailer_id: `trailer-${g}`, machines: sorted, lat: Number(newest.lat), lng: Number(newest.lng), speed_kph: Math.max(...sorted.map(m => m.current_speed_kph ?? 0)), haulerId: null, haulerName: null, synced_at: newest.synced_at ?? null });
  }
  return { trailers, absorbedIds };
}

type FlatMachine = FleetMachine & { _job_name: string; _job_id: number | null; _job_number: string | null };

interface ActivityDay {
  date: string;
  total_minutes: number;
  jobs: { job_id: number | null; job_name: string; bradanna_job_number: string | null; minutes: number }[];
}

interface ActivityData {
  equipment_id: number;
  days: number;
  total_minutes_running: number;
  days_with_runtime: number;
  by_day: ActivityDay[];
}

interface JobGeofence {
  job_id: number;
  job_name: string;
  bradanna_job_number: string | null;
  lat: number;
  lng: number;
  geofence_type: string | null;
  geofence_data: any;
}

// Canonical rental vendors. Anything not in this list falls back to a
// free-text input ("Other"). Keep alphabetised so the dropdown is predictable.
const RENTAL_COMPANIES = ['Alta', 'Dobbs', 'Kelly Tractor', 'Sunbelt Rentals', 'United Rentals'];

// Equipment sub-types — a secondary, cross-type tag (a Paver, a Roller and a
// Broom Tractor can all be "asphalt"). Set per-machine in the bfav5 Equipment
// editor (equipment_subtype). Drives the By Type sub-type cards and the By Job
// sub-group headers.
const SUBTYPES: { value: string; label: string; color: string }[] = [
  { value: 'asphalt', label: 'Asphalt', color: '#0f172a' },
  { value: 'farm', label: 'Farm', color: '#15803d' },
];

const formatHrs = (mins: number) => {
  if (mins === 0) return '—';
  if (mins < 60) return `${mins}m`;
  return `${(mins / 60).toFixed(1)}h`;
};

// Service is "due" when the machine has run >300 engine hours past its last
// service. last_inspection_hours is the server's canonical service baseline
// (BFA services/service-baseline.service.js: last inspection that CHANGED a
// service item, else last inspection). False when there's no baseline.
const SERVICE_DUE_HOURS = 300;
// Engine hours run since the last service (null = no baseline on record).
const sinceService = (m: FleetMachine): number | null =>
  m.last_inspection_hours != null && m.current_hours != null
    ? Math.round(m.current_hours - m.last_inspection_hours)
    : null;
const serviceDue = (m: FleetMachine) => (sinceService(m) ?? 0) > SERVICE_DUE_HOURS;

// Shared status-badge style: solid fill + white text so each state is bold and
// distinct at a glance (critical=red, moderate=orange, service=blue, unused=slate).
const fleetBadge: React.CSSProperties = {
  marginLeft: 8, padding: '3px 8px', borderRadius: 5, fontSize: 11, fontWeight: 800,
  textTransform: 'uppercase', letterSpacing: 0.4, whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', lineHeight: 1.1,
};

const TYPE_COLORS: Record<string, string> = {
  'Excavator': '#f59e0b', 'Mini Excavator': '#fbbf24', 'Dozer': '#d97706',
  'Skid Steer': '#3b82f6', 'Loader': '#8b5cf6', 'Backhoe': '#06b6d4',
  'Grader': '#10b981', 'Compactor': '#84cc16', 'Roller': '#ec4899',
  'Forklift': '#f97316', 'Truck': '#6366f1', 'Off Road Truck': '#15803d', 'Trailer': '#64748b',
  'Generator': '#eab308', 'Water Truck': '#14b8a6', 'Paver': '#a855f7',
  'Screener': '#ef4444', 'TN Equipment': '#22d3ee', 'Box Blade': '#0ea5e9',
  'Broom Tractor': '#c084fc', 'Personal Vehicles': '#6b7280', 'Other': '#94a3b8',
};
// Normalize a raw equipment_type so case/whitespace/plural variants collapse to
// one group. Without this, DB values like "Roller", "roller ", and "Rollers"
// render as three separate "By Type" groups. Title-cases each word and folds a
// trailing-"s" plural back onto a known singular type ("Dozers" → "Dozer").
const canonicalType = (t?: string): string => {
  let s = (t || '').trim().replace(/\s+/g, ' ');
  if (!s) return 'Other';
  s = s.replace(/\w\S*/g, w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
  if (s.endsWith('s') && TYPE_COLORS[s.slice(0, -1)]) s = s.slice(0, -1);
  return s;
};
const colorForType = (t?: string) => TYPE_COLORS[canonicalType(t)] || '#94a3b8';
// Display-only type shortener so long names don't crowd the row. Grouping,
// colors and filters still key on the full canonical type.
const displayType = (t?: string): string => (t || '').replace(/mini\s*excavator/i, 'Mini Ex');
// Model-first display name: crews know machines by model, so "Cat 255" reads
// better as "255 Cat". Moves the last digit-bearing token of the name to the
// front ("New Holland 345D" → "345D New Holland", "Ford F-150" → "F-150 Ford");
// names without a model token pass through untouched. Display-only — sorting
// and grouping still use the stored name.
const displayName = (name?: string): string => {
  const parts = (name || '').trim().split(/\s+/);
  const last = parts[parts.length - 1];
  if (parts.length < 2 || !/\d/.test(last)) return name || '';
  return [last, ...parts.slice(0, -1)].join(' ');
};

const machineColor = (m: FleetMachine) => {
  if (m.is_idle) return '#ef4444';
  if (m.ignition_on) return '#10b981';
  return '#64748b';
};

const statusBorder = (m: FleetMachine) => {
  if (m.is_idle) return '#ef4444';
  if (m.ignition_on) return '#10b981';
  return '#fff';
};

// Dead-tracker badge, computed from the HONEST freshness + voltage signals (not
// synced_at). Returns null when the tracker looks healthy.
// Fleet-wide worst legitimate gap is exactly 1.0h (hourly sleep heartbeat) —
// 3 missed beats = offline. Matches backend DEVICE_SILENT_HOURS.
const OFFLINE_HOURS = 3;
const fmtAge = (h: number) => (h >= 48 ? `${Math.round(h / 24)}d` : h >= 1 ? `${Math.round(h)}h` : `${Math.round(h * 60)}m`);
const trackerBadge = (m: FleetMachine): { text: string; color: string } | null => {
  // Flag ONLY reliable signals. Parked/off equipment sends hourly "sleep"
  // heartbeats with gps_valid=false / 0 sats — NORMAL, not a fault. Show: dead/low
  // battery (resting engine-off voltage), device truly SILENT (synced_at age), and
  // no-fix ONLY while the machine is RUNNING.
  const pv = m.resting_power_v ?? null;
  const nominal = pv != null && pv > 18 ? 24 : 12;
  const lowV = nominal === 24 ? 23.4 : 11.7;
  const critV = nominal === 24 ? 22.0 : 11.0;
  const syncAgeH = m.synced_at ? (Date.now() - new Date(m.synced_at).getTime()) / 3.6e6 : null;
  if (pv != null && pv < critV) return { text: `⚠ ${pv.toFixed(1)}V`, color: '#dc2626' };
  if (syncAgeH != null && syncAgeH >= OFFLINE_HOURS) return { text: `Offline ${fmtAge(syncAgeH)}`, color: '#dc2626' };
  if (pv != null && pv < lowV) return { text: `Low ${pv.toFixed(1)}V`, color: '#f59e0b' };
  if (m.no_fix) return { text: 'No fix', color: '#f59e0b' };   // running a while with zero valid fixes (not a startup warm-up)
  return null;
};
const badgeHtml = (m: FleetMachine): string => {
  const b = trackerBadge(m);
  return b
    ? ` <span style="background:${b.color};color:#fff;padding:1px 5px;border-radius:3px;font-size:10px;font-weight:700;">${b.text}</span>`
    : '';
};

function circleToPolygon(lat: number, lng: number, radiusMeters: number, points = 32): [number, number][] {
  const coords: [number, number][] = [];
  const earthRadius = 6371000;
  const latRad = (lat * Math.PI) / 180;
  for (let i = 0; i <= points; i++) {
    const angle = (i / points) * 2 * Math.PI;
    const dx = (radiusMeters * Math.cos(angle)) / earthRadius;
    const dy = (radiusMeters * Math.sin(angle)) / earthRadius;
    coords.push([lng + ((dy * 180) / Math.PI) / Math.cos(latRad), lat + (dx * 180) / Math.PI]);
  }
  return coords;
}

const FleetView: React.FC = () => {
  const navigate = useNavigate();
  const auth = useContext(AuthContext);
  const isAdmin = auth?.user?.role === 'admin';

  const [data, setData] = useState<FleetData | null>(null);
  const [activeJobs, setActiveJobs] = useState<JobGeofence[]>([]);
  const [loading, setLoading] = useState(true);
  const [resuming, setResuming] = useState(false);   // "updating positions" pill after the app resumes
  const [error, setError] = useState<string | null>(null);
  // Tab state lives in URL so browser/page back walks tab history (Unused → By Job).
  const [searchParams, setSearchParams] = useSearchParams();
  type Tab = 'list' | 'types' | 'rentals' | 'unused' | 'map';
  const tabFromUrl = (searchParams.get('tab') as Tab) || 'list';
  const tab: Tab = (['list', 'types', 'rentals', 'unused', 'map'].includes(tabFromUrl) ? tabFromUrl : 'list') as Tab;
  const setTab = (next: Tab) => {
    const params = new URLSearchParams(searchParams);
    params.set('tab', next);
    setSearchParams(params);
  };
  const [selectedType, setSelectedType] = useState<string>('');
  const [jobsMode, setJobsMode] = useState<'active' | 'all' | 'none'>('active');
  const [drawerEquipmentId, setDrawerEquipmentId] = useState<number | null>(null);
  // Ref mirror so long-lived closures (SSE resume handler) see the live value.
  const drawerRef = useRef<number | null>(null); drawerRef.current = drawerEquipmentId;
  // Equipment whose exact GPS position is currently emphasized with a pulsing
  // ping (set when a machine is located via the crosshair button or selected).
  const [highlightedId, setHighlightedId] = useState<number | null>(null);
  // Bumped whenever Mapbox (re)loads its style — e.g. after a mobile WebGL
  // context loss wipes all custom sources/layers. Re-runs the map effect so the
  // geofence shapes/cluster/leader layers get re-added instead of staying gone
  // until the app is force-closed.
  const [styleEpoch, setStyleEpoch] = useState(0);
  // Issue popup: the machine whose logged issue is being shown (null = closed).
  const [issuePopup, setIssuePopup] = useState<FleetMachine | null>(null);
  const [activity, setActivity] = useState<ActivityData | null>(null);
  const [activityLoading, setActivityLoading] = useState(false);

  // Rental management state
  const [rentals, setRentals] = useState<RentalRow[]>([]);
  const [rentalsLoading, setRentalsLoading] = useState(false);
  const [showRentalModal, setShowRentalModal] = useState(false);
  const [editingRental, setEditingRental] = useState<RentalRow | null>(null);
  const [rentalForm, setRentalForm] = useState({
    equipment_type: '', description: '', rental_company: '', vendor_equipment_id: '',
    job_id: '' as string | number, notes: '',
  });
  // 'select' = picking from the known vendor list; 'other' = typing a custom name.
  const [companyMode, setCompanyMode] = useState<'select' | 'other'>('select');
  const [rentalSaving, setRentalSaving] = useState(false);
  // Focused "Move rental" modal — opened by tapping a rental row in By Job /
  // By Type tabs (full edit lives in the Rentals tab).
  const [movingRental, setMovingRental] = useState<RentalRow | null>(null);
  const [moveTargetJobId, setMoveTargetJobId] = useState<string | number>('');
  const [moveSaving, setMoveSaving] = useState(false);
  const openMoveRental = (r: RentalRow) => {
    setMovingRental(r);
    setMoveTargetJobId(r.job_id ?? '');
  };
  const submitMoveRental = async () => {
    if (!movingRental) return;
    setMoveSaving(true);
    try {
      await apiCall(`/rentals/${movingRental.rental_id}`, 'PATCH', {
        job_id: moveTargetJobId ? Number(moveTargetJobId) : null,
      });
      setMovingRental(null);
      await loadRentals();
      await load();
    } catch (e: any) {
      alert(`Failed: ${e.message}`);
    } finally {
      setMoveSaving(false);
    }
  };

  const mapContainer = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  // Auto fit-to-all-bounds should happen ONCE on first load. Without this guard
  // the effect below re-fits on every SSE position push, yanking the camera
  // back out right after a user clicks to zoom in.
  const didInitialFitRef = useRef(false);
  // Follow-cam: while set, the map eases to keep this machine centered on each
  // live position push. Armed when a machine is selected/located; released when
  // the user pans the map themselves (gated on a real gesture). Fixes the
  // "selected truck drifts out of view as it moves" bug.
  const followingIdRef = useRef<number | null>(null);
  // Pulsing "exact position" ping for the highlighted machine.
  const pingMarkerRef = useRef<mapboxgl.Marker | null>(null);
  // Keyed marker map (one entry per equipment_id). Lets us update position
  // smoothly via requestAnimationFrame interpolation instead of destroying and
  // recreating every marker on each state change. Other markers don't flicker
  // when one moves, and the marker that moved slides smoothly to its new spot.
  interface MarkerEntry {
    marker: mapboxgl.Marker;
    el: HTMLDivElement;
    dotEl: HTMLElement | null;
    labelEl: HTMLElement | null;
    lng: number;
    lat: number;
    targetLng: number;
    targetLat: number;
    animFrame: number | null;
    lastIgnition: boolean;
    lastIdle: boolean;
    lastType: string;
    lastTransit?: string;   // transit_state at last paint, so we restyle on change
    lastBadge?: string;     // dead-tracker badge html at last paint, so it refreshes in place
    speedEl?: HTMLElement | null; // trailer markers: speed badge to update in place
    lastFixAt?: number;     // when the last fix landed, to time the smoothing glide
    labelLeft?: boolean;    // which side the label hangs (west-half machines → left)
    srcLng?: number;        // true GPS at last paint — glide only when THIS changes
    srcLat?: number;
    buf?: { t: number; lng: number; lat: number }[];   // Option 2 fix-time playback (truck markers only)
    clockFixT?: number; clockArr?: number;
  }
  const markersByIdRef = useRef<Map<number, MarkerEntry>>(new Map());
  const snapNextRef = useRef(false);   // jump markers (no glide) on next render — set on resume
  // Keyed trailer/in-transit markers (one per cluster), separate from per-machine.
  const transitMarkersRef = useRef<Map<string, MarkerEntry>>(new Map());
  // Sticky trailer membership: equipment_id → stable groupId. Machines that go
  // in_transit together stay grouped (no distance check) until one leaves
  // in_transit (engine on / parked). See clusterTransit.
  const stickyTransitRef = useRef<Map<number, number>>(new Map());
  // Empty-truck (lowboy) markers, keyed by hauler equipment_id.
  const haulerMarkersRef = useRef<Map<number, MarkerEntry>>(new Map());
  // Keyed by job_id so labels persist across reloads instead of being destroyed
  // and recreated on every SSE/poll update (which made them flash as units moved).
  const jobMarkersRef = useRef<Map<number, mapboxgl.Marker>>(new Map());
  // Handoff zoom between cluster bubbles (below) and labeled HTML markers (at/above).
  // 14 means clusters persist to roughly block-level zoom — labels only appear
  // once you've zoomed onto a specific job site.
  const LABEL_MIN_ZOOM = 14;
  // Pixel radius for grouping markers into a cluster. Larger = more aggressive
  // grouping (fewer visible bubbles, each holding more machines).
  const CLUSTER_RADIUS = 110;

  // requestAnimationFrame-driven lerp from the marker's current lng/lat to the
  // new target over ~500ms with ease-out. Cancels any in-flight animation so
  // rapid updates always glide to the latest position without queueing.
  const animateMarker = (entry: MarkerEntry, toLng: number, toLat: number, forceSnap = false) => {
    if (entry.animFrame != null) cancelAnimationFrame(entry.animFrame);
    const fromLng = entry.lng;
    const fromLat = entry.lat;
    entry.targetLng = toLng;
    entry.targetLat = toLat;
    // First render after a resume, OR a re-spread where the true GPS didn't move
    // (zoom/fan/load): jump straight to the position so labels appear instantly
    // instead of gliding in. Real position fixes still glide (forceSnap=false).
    if (snapNextRef.current || forceSnap) {
      entry.animFrame = null;
      entry.marker.setLngLat([toLng, toLat]);
      entry.lng = toLng; entry.lat = toLat; entry.lastFixAt = performance.now();
      return;
    }
    if (Math.abs(toLng - fromLng) < 1e-9 && Math.abs(toLat - fromLat) < 1e-9) return;
    const start = performance.now();
    // Glide LINEARLY over the real gap since the previous fix (clamped 1.5–12s),
    // so the marker moves continuously at ~true pace instead of darting then
    // freezing until the next update. Renders ~one update interval behind live.
    const dt = entry.lastFixAt != null ? start - entry.lastFixAt : 0;
    entry.lastFixAt = start;
    const duration = Math.min(12000, Math.max(1500, dt || 10000));
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration);   // linear = constant speed
      const lng = fromLng + (toLng - fromLng) * t;
      const lat = fromLat + (toLat - fromLat) * t;
      entry.marker.setLngLat([lng, lat]);
      entry.lng = lng;
      entry.lat = lat;
      if (t < 1) {
        entry.animFrame = requestAnimationFrame(tick);
      } else {
        entry.animFrame = null;
      }
    };
    entry.animFrame = requestAnimationFrame(tick);
  };

  // ── Option 2 smooth playback — TRUCK markers only (trailers + haulers) ───────
  // Render each truck ~PB_DELAY_MS behind its latest GPS fix, interpolating
  // buffered fixes keyed by real fix time (synced_at). Per-machine EQUIPMENT
  // markers stay on the glide above — only road-driving trucks need this.
  const PB_DELAY_MS = 11000;
  const pbInit = (lng: number, lat: number, fixT: number) => ({ buf: [{ t: fixT, lng, lat }], clockFixT: fixT, clockArr: performance.now() });
  const pbPush = (e: MarkerEntry, lng: number, lat: number, fixT: number) => {
    if (!e.buf) e.buf = [];
    const buf = e.buf;
    const last = buf[buf.length - 1];
    if (last && fixT <= last.t) return;   // stale / duplicate fix — ignore
    if (!last || fixT - last.t > 30000) { buf.length = 0; buf.push({ t: fixT, lng, lat }); }   // first move / resume → fresh
    else { buf.push({ t: fixT, lng, lat }); const cut = fixT - PB_DELAY_MS - 4000; while (buf.length > 2 && buf[1].t < cut) buf.shift(); }
    e.clockFixT = fixT; e.clockArr = performance.now();
  };
  const pbRender = (e: MarkerEntry, nowMs: number) => {
    const b = e.buf;
    if (!b || b.length === 0 || e.clockFixT == null || e.clockArr == null) return;
    const renderT = e.clockFixT + (nowMs - e.clockArr) - PB_DELAY_MS;
    let lng: number, lat: number;
    if (renderT <= b[0].t) { lng = b[0].lng; lat = b[0].lat; }
    else if (renderT >= b[b.length - 1].t) { const p = b[b.length - 1]; lng = p.lng; lat = p.lat; }
    else { let i = 0; while (i < b.length - 1 && b[i + 1].t <= renderT) i++; const a = b[i], c = b[i + 1]; const f = c.t === a.t ? 1 : (renderT - a.t) / (c.t - a.t); lng = a.lng + (c.lng - a.lng) * f; lat = a.lat + (c.lat - a.lat) * f; }
    if (Math.abs(lng - e.lng) > 1e-9 || Math.abs(lat - e.lat) > 1e-9) { e.marker.setLngLat([lng, lat]); e.lng = lng; e.lat = lat; }
  };
  const pbRafRef = useRef<number | null>(null);
  useEffect(() => {
    const tick = () => {
      const nowMs = performance.now();
      for (const e of transitMarkersRef.current.values()) pbRender(e, nowMs);
      for (const e of haulerMarkersRef.current.values()) pbRender(e, nowMs);
      pbRafRef.current = requestAnimationFrame(tick);
    };
    pbRafRef.current = requestAnimationFrame(tick);
    return () => { if (pbRafRef.current != null) cancelAnimationFrame(pbRafRef.current); };
  }, []);

  const updateLabelVisibility = () => {
    if (!mapRef.current) return;
    const visible = mapRef.current.getZoom() >= LABEL_MIN_ZOOM;
    // Hide whole marker (dot + label) below threshold; the Mapbox GeoJSON
    // cluster layer takes over so we don't pile labels on top of each other.
    for (const entry of markersByIdRef.current.values()) {
      entry.el.style.display = visible ? '' : 'none';
    }
    document.querySelectorAll<HTMLElement>('.fleet-marker-label').forEach(el => {
      el.style.display = visible ? '' : 'none';
    });
  };

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('bfa_token')}` };
      // fetchWithTimeout auto-retries GET on suspended-socket / timeout — see
      // utils/network.utils.ts. Symptom we're avoiding: first call after the
      // PWA wakes from background returns "Failed to fetch".
      const [fleetRes, jobsRes] = await Promise.all([
        fetchWithTimeout(`${API_BASE_URL}/api/fleet/locations`, { headers }),
        fetchWithTimeout(`${API_BASE_URL}/api/jobs`, { headers }),
      ]);
      if (!fleetRes.ok) throw new Error(`Fleet HTTP ${fleetRes.status}`);
      const fleetJson = await fleetRes.json();
      setData(prev => {
        const fresh = fleetJson.data as FleetData;
        // First load, or resume (snapNextRef) → take the fresh snapshot wholesale.
        if (!prev || snapNextRef.current) return fresh;
        // Otherwise the 30s reload must NOT clobber a position the live SSE stream
        // already advanced past — keep whichever fix is newer per unit (by device
        // GPS time). Grouping/status still come from the fresh snapshot.
        const cur = new Map<number, any>();
        for (const g of prev.groups) for (const eq of g.equipment) cur.set(eq.equipment_id, eq);
        for (const h of (prev.haulers || [])) cur.set(h.equipment_id, h);
        const curNewer = (c: any, f: any) => {
          const ct = c?.synced_at ? new Date(c.synced_at).getTime() : 0;
          const ft = f?.synced_at ? new Date(f.synced_at).getTime() : 0;
          return ct && ft && ct > ft;
        };
        return {
          ...fresh,
          groups: fresh.groups.map(g => ({
            ...g,
            equipment: g.equipment.map((eq: any) => {
              const c = cur.get(eq.equipment_id);
              return (c && curNewer(c, eq))
                ? { ...eq, lat: c.lat, lng: c.lng, ignition_on: c.ignition_on, current_speed_kph: c.current_speed_kph, synced_at: c.synced_at }
                : eq;
            }),
          })),
          haulers: (fresh.haulers || []).map((h: any) => {
            const c = cur.get(h.equipment_id);
            return (c && curNewer(c, h))
              ? { ...h, lat: c.lat, lng: c.lng, current_speed_kph: c.current_speed_kph, moving: c.moving ?? h.moving, synced_at: c.synced_at }
              : h;
          }),
        };
      });
      if (jobsRes.ok) {
        const jobsJson = await jobsRes.json();
        const jobs = (jobsJson?.data || jobsJson || [])
          .filter((j: any) => j.is_active && j.lat != null && j.lng != null)
          .map((j: any) => ({
            job_id: j.job_id, job_name: j.job_name, bradanna_job_number: j.bradanna_job_number,
            lat: Number(j.lat), lng: Number(j.lng), geofence_type: j.geofence_type,
            geofence_data: typeof j.geofence_data === 'string' ? JSON.parse(j.geofence_data) : j.geofence_data,
          }));
        setActiveJobs(jobs);
      }
    } catch (e: any) {
      // Switching cellular↔wifi aborts the in-flight request — it surfaces as
      // "signal is aborted" / "Failed to fetch". That's transient, not a real
      // failure: don't flash an error banner (keep showing the last data), just
      // retry shortly. Only genuine errors (HTTP status, parse) show a banner.
      const msg = e?.message || '';
      const transient = e?.name === 'AbortError' || e instanceof TypeError || /abort|failed to fetch|network/i.test(msg);
      if (transient) {
        console.warn('[fleet] transient load error, retrying:', msg);
        setTimeout(() => { if (document.visibilityState !== 'hidden') load(); }, 2500);
      } else {
        setError(msg || 'Failed to load fleet');
      }
    } finally {
      setLoading(false);
      setResuming(false);
    }
  };

  useEffect(() => { if (isAdmin) load(); }, [isAdmin]);

  // Log a "fleet" page visit so the bfav5 Fleet Activity counter reflects PWA
  // opens too (same DASHBOARD_VISITS table, same {page:'fleet'} key — bfav5's
  // GET /admin/dashboard/visits?page=fleet aggregates both producers).
  useEffect(() => {
    if (!isAdmin) return;
    fetchWithTimeout(`${API_BASE_URL}/api/admin/dashboard/visit`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('bfa_token')}`,
      },
      body: JSON.stringify({ page: 'fleet', source: 'pwa' }),
    }).catch(() => { /* analytics-only; don't bother the user */ });
  }, [isAdmin]);
  // Refresh fleet + rentals every time the user switches tabs so they always
  // see the latest data without needing to tap the refresh button.
  useEffect(() => {
    if (!isAdmin) return;
    load();
    loadRentals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // Rental handlers
  const loadRentals = async () => {
    setRentalsLoading(true);
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('bfa_token')}` };
      const res = await fetchWithTimeout(`${API_BASE_URL}/api/rentals?active=true`, { headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const j = await res.json();
      setRentals(j.data || []);
    } catch (e: any) {
      console.warn('load rentals failed', e);
    } finally {
      setRentalsLoading(false);
    }
  };
  useEffect(() => { if (isAdmin) loadRentals(); }, [isAdmin]);
  useEffect(() => { if (isAdmin && tab === 'rentals') loadRentals(); }, [tab, isAdmin]);

  const openAddRental = () => {
    setEditingRental(null);
    setRentalForm({ equipment_type: '', description: '', rental_company: '', vendor_equipment_id: '', job_id: '', notes: '' });
    setCompanyMode('select');
    setShowRentalModal(true);
  };
  const openEditRental = (r: RentalRow) => {
    setEditingRental(r);
    setRentalForm({
      equipment_type: r.equipment_type, description: r.description || '',
      rental_company: r.rental_company, vendor_equipment_id: r.vendor_equipment_id || '',
      job_id: r.job_id ?? '', notes: r.notes || '',
    });
    // If the saved company isn't one of the known vendors, default to free-text mode
    setCompanyMode(RENTAL_COMPANIES.includes(r.rental_company) ? 'select' : 'other');
    setShowRentalModal(true);
  };
  const apiCall = async (path: string, method: string, body?: any) => {
    const headers: any = { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('bfa_token')}` };
    // POST/PUT/DELETE never auto-retry (could double-spend), but the timeout
    // still applies so a hung socket doesn't lock the UI forever.
    const res = await fetchWithTimeout(`${API_BASE_URL}/api${path}`, { method, headers, body: body ? JSON.stringify(body) : undefined });
    const j = await res.json().catch(() => ({}));
    if (!res.ok || j.success === false) throw new Error(j.error || `HTTP ${res.status}`);
    return j;
  };
  const saveRental = async () => {
    if (!rentalForm.equipment_type.trim() || !rentalForm.rental_company.trim()) {
      alert('Equipment type and rental company are required');
      return;
    }
    setRentalSaving(true);
    try {
      const body: any = {
        equipment_type: rentalForm.equipment_type.trim(),
        description: rentalForm.description.trim() || null,
        rental_company: rentalForm.rental_company.trim(),
        vendor_equipment_id: rentalForm.vendor_equipment_id.trim() || null,
        job_id: rentalForm.job_id ? Number(rentalForm.job_id) : null,
        notes: rentalForm.notes.trim() || null,
      };
      if (editingRental) await apiCall(`/rentals/${editingRental.rental_id}`, 'PATCH', body);
      else await apiCall('/rentals', 'POST', body);
      setShowRentalModal(false);
      await loadRentals();
      await load();
    } catch (e: any) {
      alert(`Failed: ${e.message}`);
    } finally {
      setRentalSaving(false);
    }
  };
  const moveRental = async (r: RentalRow, newJobId: number | null) => {
    try { await apiCall(`/rentals/${r.rental_id}`, 'PATCH', { job_id: newJobId }); await loadRentals(); await load(); }
    catch (e: any) { alert(`Failed: ${e.message}`); }
  };
  const returnRental = async (r: RentalRow) => {
    if (!confirm(`Mark "${r.description || r.equipment_type}" (${r.rental_company}) as returned?`)) return;
    try { await apiCall(`/rentals/${r.rental_id}/return`, 'POST'); await loadRentals(); await load(); }
    catch (e: any) { alert(`Failed: ${e.message}`); }
  };
  const deleteRental = async (r: RentalRow) => {
    if (!confirm('Permanently delete this rental? This cannot be undone.')) return;
    try { await apiCall(`/rentals/${r.rental_id}`, 'DELETE'); await loadRentals(); await load(); }
    catch (e: any) { alert(`Failed: ${e.message}`); }
  };
  // Row click: rentals open the focused move modal; owned equipment opens the drawer.
  // Full rental edit is reachable via the dedicated Rentals tab.
  const handleRowClick = (m: FleetMachine) => {
    if (m.is_rental && m.rental_id) {
      const r = rentals.find(rr => rr.rental_id === m.rental_id);
      if (r) openMoveRental(r);
    } else {
      openDrawer(m.equipment_id);
    }
  };

  // Real-time fleet updates via Server-Sent Events. Backend's Traccar webhook
  // broadcasts on every position; we mutate the relevant equipment in place so
  // the map marker slides + the list row updates without losing scroll/zoom.
  useEffect(() => {
    if (!isAdmin) return;
    const token = localStorage.getItem('bfa_token');
    if (!token) return;
    const url = `${API_BASE_URL}/api/fleet/stream?token=${encodeURIComponent(token)}`;
    const es = new EventSource(url);
    es.addEventListener('message', (e) => {
      try {
        const u = JSON.parse(e.data);
        if (u?.type !== 'position' || !u.equipment_id) return;
        setData(prev => {
          if (!prev) return prev;
          let movedTo: number | null = null;
          // Apply delta to the equipment in whichever group currently holds it
          const groupsAfterUpdate = prev.groups.map(g => ({
            ...g,
            equipment: g.equipment.map(eq => {
              if (eq.equipment_id !== u.equipment_id) return eq;
              // Don't move the marker backward to an older fix (out-of-order /
              // buffered catch-up push). synced_at is now the device GPS time.
              const uT = u.synced_at ? new Date(u.synced_at).getTime() : 0;
              const eT = eq.synced_at ? new Date(eq.synced_at).getTime() : 0;
              if (uT && eT && uT < eT) return eq;
              movedTo = u.computed_job_id ?? null;
              return {
                ...eq,
                lat: u.lat ?? eq.lat,
                lng: u.lng ?? eq.lng,
                ignition_on: !!u.ignition_on,
                current_hours: u.hours_total ?? eq.current_hours,
                // Carry live speed so a moving machine's mph updates between
                // polls (transit_state itself still refreshes on the 30s poll).
                current_speed_kph: u.speed_kph ?? eq.current_speed_kph,
                synced_at: u.synced_at ?? eq.synced_at,
              };
            }),
          }));
          // Also apply the delta to the matching HAULER (tracked truck). Haulers
          // live in prev.haulers (not groups), so without this the truck's pin +
          // label lagged at the last-reload position while the equipment dots
          // streamed live — the trailer it anchors only catches up on reload.
          const haulersAfterUpdate = (prev.haulers || []).map(h => {
            if (h.equipment_id !== u.equipment_id) return h;
            // Same backward-guard as the equipment dots so the truck pin doesn't bounce.
            const uT = u.synced_at ? new Date(u.synced_at).getTime() : 0;
            const hT = h.synced_at ? new Date(h.synced_at).getTime() : 0;
            if (uT && hT && uT < hT) return h;
            const sk = u.speed_kph ?? h.current_speed_kph ?? 0;
            return {
              ...h,
              lat: u.lat ?? h.lat,
              lng: u.lng ?? h.lng,
              current_speed_kph: sk,
              moving: sk * 0.621371 >= 3,
              synced_at: u.synced_at ?? h.synced_at,
            };
          });
          // Recompute running count
          const runningCount = groupsAfterUpdate
            .flatMap(g => g.equipment)
            .filter(eq => eq.ignition_on).length;
          return {
            ...prev,
            groups: groupsAfterUpdate,
            haulers: haulersAfterUpdate,
            totals: { ...prev.totals, running_count: runningCount },
          };
          // (movedTo is unused — group re-bucketing on job change is left to the
          //  next load() since geofence boundaries are recomputed server-side.)
        });
        // Follow-cam: keep a selected machine centered as it moves. Done outside
        // setData so it runs once per event. easeTo's own movestart carries no
        // originalEvent, so the user-pan release below doesn't fire on it.
        if (followingIdRef.current === u.equipment_id && mapRef.current
            && Number.isFinite(u.lng) && Number.isFinite(u.lat)) {
          mapRef.current.easeTo({ center: [u.lng, u.lat], duration: 800 });
        }
      } catch (err) {
        console.warn('[fleet stream] bad event', err);
      }
    });
    es.addEventListener('error', () => {
      // EventSource auto-reconnects, but also force a full reload so a
      // missed OFF event during a dropped connection isn't stuck on the
      // old running-state forever. Service workers / iOS Safari can
      // silently break EventSource without firing 'open' on reconnect.
      console.warn('[fleet stream] disconnected, will reconnect + refresh');
      load();
    });
    // When the PWA returns from background (phone unlock, tab switch),
    // EventSource may be in a stale-connected state where new events
    // aren't being received. Always pull a fresh snapshot on resume.
    const onVisible = () => {
      if (document.visibilityState === 'hidden') return;
      snapNextRef.current = true;   // jump markers to the fresh fix (no glide-forward flash)
      setResuming(true);            // show "updating positions" until the fresh fetch lands
      load();
      // On resume the map canvas can resize / lose its GL context, leaving the
      // focused machine off-screen ("you see it then it disappears"). If a
      // machine drawer is open, resize + recenter on its marker so it stays framed.
      const id = drawerRef.current;
      if (id != null) {
        setTimeout(() => {
          const map = mapRef.current; if (!map) return;
          map.resize();
          const ent = markersByIdRef.current.get(id);
          if (ent && Number.isFinite(ent.targetLng) && Number.isFinite(ent.targetLat)) {
            map.easeTo({ center: [ent.targetLng, ent.targetLat], zoom: Math.max(map.getZoom(), 14), duration: 400 });
          }
        }, 350);
      }
    };
    // visibilitychange alone isn't reliable on iOS PWAs returning from
    // background — also listen for focus + pageshow (bfcache restore) so a
    // reopened app always pulls fresh data instead of showing stale state.
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    window.addEventListener('pageshow', onVisible);
    // Reload the instant connectivity returns (e.g. wifi connects after a
    // cellular→wifi switch aborted the in-flight request).
    window.addEventListener('online', onVisible);
    // Belt-and-suspenders polling. SSE on the PWA has been flaky (works
    // on bfav5 but not here — likely SW intercept). Every 30s pull the
    // current snapshot so state converges even if SSE silently dies.
    const poll = window.setInterval(() => load(), 30_000);
    return () => {
      es.close();
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
      window.removeEventListener('pageshow', onVisible);
      window.removeEventListener('online', onVisible);
      window.clearInterval(poll);
    };
  }, [isAdmin]);

  const allMachines = useMemo<FlatMachine[]>(() => {
    if (!data) return [];
    return data.groups.flatMap(g => g.equipment.map(e => ({
      ...e, _job_name: g.job_name, _job_id: g.job_id, _job_number: g.bradanna_job_number,
    })));
  }, [data]);

  const equipmentTypes = useMemo(() => {
    const set = new Set<string>();
    allMachines.forEach(m => { if (m.equipment_type) set.add(canonicalType(m.equipment_type)); });
    return [...set].sort();
  }, [allMachines]);

  // The By Type dropdown filter carries onto the map (markers, low-zoom
  // clusters, leader lines) — '' (All Equipment) = whole fleet. Mirrors bfav5.
  const mapMachines = useMemo(
    () => selectedType ? allMachines.filter(m => canonicalType(m.equipment_type) === selectedType) : allMachines,
    [allMachines, selectedType]
  );

  // In-transit machines for the "By Job" list. Like bfav5: pull them OUT of
  // their geofence group (a machine on the road sits in no geofence, so the
  // server buckets it under "Off-site" — misleading) and show them in a
  // dedicated "In Transit" section instead.
  const inTransitMachines = useMemo(
    () => (data?.groups.flatMap(g => g.equipment) || []).filter(m => m.transit_state === 'in_transit' && !m.is_rental),
    [data]
  );
  // Trailer grouping for the In Transit LIST — hauler-anchored loads show under
  // the actual truck ("Pete 589"), matching the map's trailer markers and bfav5.
  const listTrailers = useMemo(
    () => data ? clusterTransit(allMachines, data.haulers || [], stickyTransitRef.current).trailers : [],
    [data, allMachines]
  );
  const jobGroups = useMemo(() => {
    let groups = data?.groups || [];
    if (inTransitMachines.length > 0) {
      const ids = new Set(inTransitMachines.map(m => m.equipment_id));
      groups = groups
        .map(g => ({ ...g, equipment: g.equipment.filter(e => !ids.has(e.equipment_id)) }))
        .filter(g => g.equipment.length > 0);
    }
    // Unknown (no geofence) floats to the TOP — it's the group that needs eyes.
    // The Shop sinks to the BOTTOM — parked machines are the least interesting.
    // Real job sites keep the server's order in between (sort is stable).
    const rank = (g: FleetGroup) => {
      const n = (g.job_name || '').toLowerCase();
      if (g.job_id === null || n.includes('unknown')) return 0;
      if (n.includes('shop')) return 2;
      return 1;
    };
    return [...groups].sort((a, b) => rank(a) - rank(b));
  }, [data, inTransitMachines]);

  useEffect(() => {
    // Default to '' = All Equipment (no auto-select); By Type now groups by type
    // the same way By Job groups by job site.
  }, [equipmentTypes, selectedType]);

  const typeGroups = useMemo(() => {
    const map = new Map<string, FlatMachine[]>();
    for (const m of allMachines) {
      const key = canonicalType(m.equipment_type);
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(m);
    }
    for (const arr of map.values()) {
      arr.sort((a, b) => (a.equipment_name || '').localeCompare(b.equipment_name || ''));
    }
    return [...map.entries()]
      .map(([type, equipment]) => ({ type, equipment }))
      .sort((a, b) => a.type.localeCompare(b.type));
  }, [allMachines]);

  const machinesOfSelectedType = useMemo(() => {
    if (!selectedType) return [];
    return allMachines
      .filter(m => canonicalType(m.equipment_type) === selectedType)
      .sort((a, b) => (a.equipment_name || '').localeCompare(b.equipment_name || ''));
  }, [allMachines, selectedType]);

  // ---- Sub-type grouping (asphalt/farm), shared by By Job + By Type ----
  const machineRow = (m: FleetMachine, showLocation?: boolean) => (
    <MachineRow key={m.equipment_id} m={m} onClick={() => handleRowClick(m)} showLocation={showLocation} onLocate={() => flyToMachine(m)} onShowIssue={() => setIssuePopup(m)} />
  );
  const subHeaderRow = (key: string, label: string, color: string, count: number) => (
    <div key={`sub-${key}`} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', background: '#f8fafc', borderBottom: '1px solid #f1f5f9' }}>
      <span style={{ width: 8, height: 8, borderRadius: 2, background: color }} />
      <span style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase' as const, letterSpacing: 0.6, color }}>{label}</span>
      <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600 }}>{count} {count === 1 ? 'machine' : 'machines'}</span>
    </div>
  );
  // By Job: lump asphalt/farm machines together within a job; the untagged
  // remainder falls under a "Site" header. A job with no tagged machines renders
  // exactly as before (no headers).
  const renderJobEquipment = (equipment: FleetMachine[]) => {
    const order = SUBTYPES.map(s => s.value);
    const tagged = SUBTYPES
      .map(s => ({ meta: s, machines: equipment.filter(m => (m.equipment_subtype || '') === s.value) }))
      .filter(x => x.machines.length > 0);
    const untagged = equipment.filter(m => !order.includes(m.equipment_subtype || ''));
    if (tagged.length === 0) return equipment.map(m => machineRow(m));
    const out: React.ReactNode[] = [];
    tagged.forEach(t => {
      out.push(subHeaderRow(t.meta.value, t.meta.label, t.meta.color, t.machines.length));
      t.machines.forEach(m => out.push(machineRow(m)));
    });
    if (untagged.length > 0) {
      out.push(subHeaderRow('site', 'Site', '#0f172a', untagged.length));
      untagged.forEach(m => out.push(machineRow(m)));
    }
    return out;
  };
  // By Type: same subtype lumping as By Job (Asphalt/Farm sub-headers, untagged
  // under "Site") but WITHIN each type card, and rows show the location line.
  const renderTypeEquipment = (equipment: FleetMachine[]) => {
    const order = SUBTYPES.map(s => s.value);
    const tagged = SUBTYPES
      .map(s => ({ meta: s, machines: equipment.filter(m => (m.equipment_subtype || '') === s.value) }))
      .filter(x => x.machines.length > 0);
    const untagged = equipment.filter(m => !order.includes(m.equipment_subtype || ''));
    if (tagged.length === 0) return equipment.map(m => machineRow(m, true));
    const out: React.ReactNode[] = [];
    tagged.forEach(t => {
      out.push(subHeaderRow(t.meta.value, t.meta.label, t.meta.color, t.machines.length));
      t.machines.forEach(m => out.push(machineRow(m, true)));
    });
    if (untagged.length > 0) {
      out.push(subHeaderRow('site', 'Site', '#0f172a', untagged.length));
      untagged.forEach(m => out.push(machineRow(m, true)));
    }
    return out;
  };

  const unusedMachines = useMemo(
    () => allMachines.filter(m => m.is_idle).sort((a, b) => (a.equipment_name || '').localeCompare(b.equipment_name || '')),
    [allMachines]
  );

  // Map setup runs once and stays mounted; tab switch toggles visibility
  useEffect(() => {
    if (!mapContainer.current || !data) return;
    if (!mapRef.current) {
      mapRef.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/streets-v12',
        center: [-81.7, 26.2], zoom: 9,
      });
      mapRef.current.addControl(new mapboxgl.NavigationControl(), 'top-right');
      mapRef.current.on('zoom', updateLabelVisibility);
      // Release follow-cam when the USER drags/zooms the map. Programmatic
      // easeTo/flyTo also fire movestart but without originalEvent, so gating on
      // it avoids cancelling our own follow animation.
      mapRef.current.on('movestart', (e: any) => {
        if (e.originalEvent) followingIdRef.current = null;
      });
      // When the GL style reloads (notably after a mobile WebGL context loss,
      // which wipes every addSource/addLayer), re-run this effect so the
      // geofence/cluster/leader layers get re-added. Without this the orange
      // geofences vanished after the map sat open a while and only came back on
      // a force-close (which recreates the map).
      mapRef.current.on('style.load', () => setStyleEpoch(e => e + 1));
    }

    const jobIdsWithEquipment = new Set<number>(
      data.groups.filter(g => g.job_id != null && g.equipment.length > 0).map(g => g.job_id as number)
    );
    const jobsToShow = jobsMode === 'none'
      ? []
      : jobsMode === 'active'
        ? activeJobs.filter(j => jobIdsWithEquipment.has(j.job_id))
        : activeJobs;

    // Apply visual offset so multiple machines at the exact same job site
    // don't render perfectly on top of each other. Offset is pixel-aware:
    // we recompute on each zoom event so separation stays visually consistent.
    const currentZoom = mapRef.current.getZoom();
    // Group in-transit machines into trailers (one marker per trailer), and
    // keep them OUT of the per-machine pass so two machines on one trailer show
    // as a single grouped truck instead of separate flickering dots.
    const { trailers: transitTrailers, absorbedIds } = clusterTransit(mapMachines, data.haulers || [], stickyTransitRef.current);
    const spreadMachines = spreadOverlaps(mapMachines.filter(m => !absorbedIds.has(m.equipment_id)), currentZoom);

    // Keyed marker reconciliation: existing markers smoothly animate to new
    // positions, new markers fade in, removed markers fade out. No marker
    // outside the one that actually changed gets disturbed — no flicker.
    const bounds = new mapboxgl.LngLatBounds();
    const liveIds = new Set<number>();
    // Renders one machine's marker DOM. In-transit machines (server-flagged)
    // get the trailer treatment — orange truck dot + 🚛 label + mph — so a
    // machine on the move reads as "on a trailer" here too, matching the board.
    const TRUCK_SVG = `<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#0f172a" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/><path d="M15 18H9"/><path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14"/><circle cx="17" cy="18" r="2"/><circle cx="7" cy="18" r="2"/></svg>`;
    const markerInner = (m: any, dotColor: string, border: string, borderWidth: number) => {
      const inTransit = m.transit_state === 'in_transit';
      const mph = Math.round((m.current_speed_kph ?? 0) * 0.621371);
      const dot = inTransit
        ? `<span style="width:22px;height:22px;border-radius:50%;background:#f59e0b;border:2px solid #fff;box-shadow:0 0 4px rgba(0,0,0,0.4);flex-shrink:0;display:flex;align-items:center;justify-content:center;">${TRUCK_SVG}</span>`
        : `<span style="width:18px;height:18px;border-radius:50%;background:${dotColor};border:${borderWidth}px solid ${border};box-shadow:0 0 4px rgba(0,0,0,0.4);flex-shrink:0;"></span>`;
      const labelBg = inTransit ? 'rgba(245,158,11,0.95)' : 'rgba(15,23,42,0.9)';
      const labelColor = inTransit ? '#0f172a' : '#fff';
      // Only show speed when we actually have a non-zero reading — an in_transit
      // machine is often flagged by displacement with no live speed (and SSE
      // doesn't carry speed), so "0 mph" would be misleading.
      const speed = (inTransit && mph > 0) ? ` <span style="background:#0f172a;color:#fff;padding:1px 5px;border-radius:3px;font-size:11px;">${mph} mph</span>` : '';
      const label = `<span class="fleet-marker-label" style="background:${labelBg};color:${labelColor};padding:3px 7px;border-radius:4px;font-size:12px;font-weight:${inTransit ? 700 : 600};white-space:nowrap;box-shadow:0 1px 3px rgba(0,0,0,0.4);">${inTransit ? '🚛 ' : ''}${displayName(m.equipment_name)}${m.bradanna_equipment_number ? ` <span style="opacity:0.7;font-weight:500">${m.bradanna_equipment_number}</span>` : ''}${speed}${badgeHtml(m)}</span>`;
      return `${dot}${label}`;
    };

    for (const m of spreadMachines) {
      liveIds.add(m.equipment_id);
      const dotColor = colorForType(m.equipment_type);
      const border = statusBorder(m);
      const borderWidth = m.is_idle ? 3 : 2;
      const labelLeft = (m as any).labelLeft as boolean;
      let entry = markersByIdRef.current.get(m.equipment_id);
      // A marker's anchor/flex-direction can't change after creation, so rebuild it
      // when a machine flips to the other half of the ring.
      if (entry && entry.labelLeft !== labelLeft) {
        if (entry.animFrame != null) cancelAnimationFrame(entry.animFrame);
        entry.marker.remove(); markersByIdRef.current.delete(m.equipment_id); entry = undefined;
      }
      if (entry) {
        // Glide only when the machine's TRUE GPS moved (a real fix). A re-spread
        // from zoom/fan/load leaves true GPS unchanged → snap so labels appear
        // instantly instead of sliding in. Keeps the 10s live-dot glide.
        const trulyMoved = entry.srcLng != null && (Math.abs(m.lng - entry.srcLng) > 1e-9 || Math.abs(m.lat - (entry.srcLat as number)) > 1e-9);
        animateMarker(entry, m.displayLng, m.displayLat, !trulyMoved);
        entry.srcLng = m.lng; entry.srcLat = m.lat;
        // Restyle only when a visible attribute actually changed (transit_state
        // included so a machine flipping onto/off a trailer repaints in place).
        const badge = badgeHtml(m);
        if (entry.lastIgnition !== m.ignition_on || entry.lastIdle !== m.is_idle
            || entry.lastType !== m.equipment_type || entry.lastTransit !== m.transit_state
            || entry.lastBadge !== badge) {
          entry.el.innerHTML = markerInner(m, dotColor, border, borderWidth);
          const [dotEl, labelEl] = Array.from(entry.el.children) as HTMLElement[];
          entry.dotEl = dotEl; entry.labelEl = labelEl;
          entry.lastIgnition = m.ignition_on;
          entry.lastIdle = m.is_idle;
          entry.lastType = m.equipment_type;
          entry.lastTransit = m.transit_state;
          entry.lastBadge = badge;
        }
      } else {
        // First time seeing this equipment — create the marker fresh.
        // West-half machines: label hangs LEFT of the dot (row-reverse + anchor
        // right) so names point outward from the cluster, never across its center.
        const el = document.createElement('div');
        el.style.cssText = `display:flex;align-items:center;gap:6px;cursor:pointer;${labelLeft ? 'flex-direction:row-reverse;' : ''}`;
        el.innerHTML = markerInner(m, dotColor, border, borderWidth);
        el.addEventListener('click', () => openDrawer(m.equipment_id));
        const [dotEl, labelEl] = Array.from(el.children) as HTMLElement[];
        const marker = new mapboxgl.Marker({ element: el, anchor: labelLeft ? 'right' : 'left' })
          .setLngLat([m.displayLng, m.displayLat])
          .addTo(mapRef.current!);
        entry = {
          marker, el, dotEl, labelEl,
          lng: m.displayLng, lat: m.displayLat, targetLng: m.displayLng, targetLat: m.displayLat,
          animFrame: null,
          lastIgnition: m.ignition_on, lastIdle: m.is_idle, lastType: m.equipment_type, lastTransit: m.transit_state,
          lastBadge: badgeHtml(m),
          labelLeft, srcLng: m.lng, srcLat: m.lat,
        };
        markersByIdRef.current.set(m.equipment_id, entry);
      }
      bounds.extend([m.displayLng, m.displayLat]);
    }
    // Remove markers for equipment that's gone (unlinked, archived, etc.)
    for (const [id, entry] of markersByIdRef.current.entries()) {
      if (!liveIds.has(id)) {
        if (entry.animFrame != null) cancelAnimationFrame(entry.animFrame);
        entry.marker.remove();
        markersByIdRef.current.delete(id);
      }
    }

    // ─── Trailer markers (in-transit groups) ───────────────────────────────
    // One orange truck marker per trailer; label lists EACH machine on its own
    // line so you can read what's aboard. Keyed so a moving trailer slides
    // smoothly instead of recreating.
    const liveTrailerIds = new Set<string>();
    for (const t of transitTrailers) {
      liveTrailerIds.add(t.trailer_id);
      bounds.extend([t.lng, t.lat]);
      const mph = Math.round(t.speed_kph * 0.621371);
      const header = t.haulerId != null ? (t.haulerName || 'Truck') : 'In Transit';
      let entry = transitMarkersRef.current.get(t.trailer_id);
      if (entry) {
        pbPush(entry, t.lng, t.lat, t.synced_at ? new Date(t.synced_at).getTime() : Date.now());
        if (entry.speedEl) entry.speedEl.textContent = mph > 0 ? `${mph} mph` : '';
      } else {
        const el = document.createElement('div');
        el.style.cssText = `display:flex;align-items:flex-start;gap:6px;cursor:pointer;`;
        const lines = t.machines.map(mm =>
          `<div style="font-weight:600;white-space:nowrap;">${displayName(mm.equipment_name)}${mm.bradanna_equipment_number ? ` <span style="opacity:0.65;font-weight:500">${mm.bradanna_equipment_number}</span>` : ''}</div>`
        ).join('');
        el.innerHTML = `
          <span style="width:30px;height:30px;border-radius:50%;background:#f59e0b;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.4);display:flex;align-items:center;justify-content:center;flex-shrink:0;">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0f172a" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round"><path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/><path d="M15 18H9"/><path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14"/><circle cx="17" cy="18" r="2"/><circle cx="7" cy="18" r="2"/></svg>
          </span>
          <div class="fleet-marker-label" style="background:rgba(245,158,11,0.95);color:#0f172a;padding:4px 8px;border-radius:6px;font-size:12px;font-weight:700;line-height:1.35;box-shadow:0 1px 3px rgba(0,0,0,0.4);">
            <div style="display:flex;align-items:center;gap:6px;margin-bottom:2px;padding-bottom:2px;border-bottom:1px solid rgba(15,23,42,0.25);">
              🚛 ${header}
              <span class="trailer-speed" style="margin-left:auto;background:#0f172a;color:#fff;padding:1px 5px;border-radius:3px;font-size:11px;font-weight:600;">${mph > 0 ? `${mph} mph` : ''}</span>
            </div>
            ${lines}
          </div>
        `;
        const clickId = t.haulerId ?? t.machines[0]?.equipment_id;
        if (clickId != null) el.addEventListener('click', () => openDrawer(clickId));
        const labelEl = el.children[1] as HTMLElement;
        const speedEl = labelEl.querySelector('.trailer-speed') as HTMLElement;
        const marker = new mapboxgl.Marker({ element: el, anchor: 'left' }).setLngLat([t.lng, t.lat]).addTo(mapRef.current!);
        entry = {
          marker, el, dotEl: null, labelEl, speedEl,
          lng: t.lng, lat: t.lat, targetLng: t.lng, targetLat: t.lat,
          animFrame: null, lastIgnition: false, lastIdle: false, lastType: '',
          ...pbInit(t.lng, t.lat, t.synced_at ? new Date(t.synced_at).getTime() : Date.now()),
        };
        transitMarkersRef.current.set(t.trailer_id, entry);
      }
    }
    for (const [id, entry] of transitMarkersRef.current.entries()) {
      if (!liveTrailerIds.has(id)) {
        if (entry.animFrame != null) cancelAnimationFrame(entry.animFrame);
        entry.marker.remove();
        transitMarkersRef.current.delete(id);
      }
    }

    // ─── Empty-truck (lowboy) markers ──────────────────────────────────────
    // Show trucks themselves, but only ones that RAN today (idle parked trucks
    // stay off the map). A loaded truck is already drawn as its trailer, so skip
    // haulers that have a trailer. Slate truck icon vs the orange loaded trailer.
    const trailerHaulerIds = new Set(transitTrailers.filter(t => t.haulerId != null).map(t => t.haulerId));
    const liveHaulerIds = new Set<number>();
    // Bare trucks are noise when the map is filtered to a machine type — hide
    // them (loaded trailers carrying a matching machine still render above).
    for (const h of (selectedType ? [] : data.haulers || [])) {
      if (h.lat == null || h.lng == null) continue;
      if ((h.ran_today_minutes ?? 0) <= 0) continue;
      if (trailerHaulerIds.has(h.equipment_id)) continue;
      liveHaulerIds.add(h.equipment_id);
      bounds.extend([h.lng, h.lat]);
      const mph = Math.round((h.current_speed_kph ?? 0) * 0.621371);
      let entry = haulerMarkersRef.current.get(h.equipment_id);
      if (entry) {
        pbPush(entry, h.lng, h.lat, h.synced_at ? new Date(h.synced_at).getTime() : Date.now());
        if (entry.speedEl) entry.speedEl.textContent = (h.moving && mph > 0) ? `${mph} mph` : '';
      } else {
        const el = document.createElement('div');
        el.style.cssText = `display:flex;align-items:center;gap:6px;cursor:pointer;`;
        el.innerHTML = `
          <span style="width:28px;height:28px;border-radius:50%;background:#475569;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.4);display:flex;align-items:center;justify-content:center;flex-shrink:0;">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round"><path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/><path d="M15 18H9"/><path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14"/><circle cx="17" cy="18" r="2"/><circle cx="7" cy="18" r="2"/></svg>
          </span>
          <span class="fleet-marker-label" style="background:rgba(71,85,105,0.95);color:#fff;padding:3px 7px;border-radius:4px;font-size:12px;font-weight:700;white-space:nowrap;box-shadow:0 1px 3px rgba(0,0,0,0.4);">${h.equipment_name}${h.bradanna_equipment_number ? ` <span style="opacity:0.7;font-weight:500">${h.bradanna_equipment_number}</span>` : ''}<span class="hauler-speed" style="margin-left:6px;background:#0f172a;color:#fff;padding:1px 5px;border-radius:3px;font-size:11px;font-weight:600;">${(h.moving && mph > 0) ? `${mph} mph` : ''}</span></span>
        `;
        el.addEventListener('click', () => openDrawer(h.equipment_id));
        const labelEl = el.children[1] as HTMLElement;
        const speedEl = labelEl.querySelector('.hauler-speed') as HTMLElement;
        const marker = new mapboxgl.Marker({ element: el, anchor: 'left' }).setLngLat([h.lng, h.lat]).addTo(mapRef.current!);
        entry = {
          marker, el, dotEl: null, labelEl, speedEl,
          lng: h.lng, lat: h.lat, targetLng: h.lng, targetLat: h.lat,
          animFrame: null, lastIgnition: false, lastIdle: false, lastType: '',
          ...pbInit(h.lng, h.lat, h.synced_at ? new Date(h.synced_at).getTime() : Date.now()),
        };
        haulerMarkersRef.current.set(h.equipment_id, entry);
      }
    }
    for (const [id, entry] of haulerMarkersRef.current.entries()) {
      if (!liveHaulerIds.has(id)) {
        if (entry.animFrame != null) cancelAnimationFrame(entry.animFrame);
        entry.marker.remove();
        haulerMarkersRef.current.delete(id);
      }
    }
    snapNextRef.current = false;   // consumed — subsequent updates glide normally

    // ─── Cluster layer for low-zoom view ───────────────────────────────────
    // Below LABEL_MIN_ZOOM the HTML markers are hidden (see updateLabelVisibility)
    // and this Mapbox GeoJSON cluster layer takes over so dense areas show a
    // count bubble instead of a pile of overlapping labels.
    // Cluster source MUST use the real lat/lng (not the spread/offset coords).
    // The spread offset is only sensible at the zoom where HTML markers will
    // be visible — at very low zoom the offset multiplies to hundreds of km
    // and Mapbox would cluster machines into the wrong county.
    const fleetFC = {
      type: 'FeatureCollection' as const,
      features: mapMachines
        // Skip in-trailer machines — they're shown by their trailer marker, not
        // as cluster bubbles (avoids double-counting at low zoom).
        .filter(m => Number.isFinite(m.lat) && Number.isFinite(m.lng) && !absorbedIds.has(m.equipment_id))
        .map(m => ({
          type: 'Feature' as const,
          properties: { equipment_id: m.equipment_id, color: machineColor(m) },
          geometry: { type: 'Point' as const, coordinates: [m.lng, m.lat] },
        })),
    };
    const setupCluster = () => {
      const map = mapRef.current!;
      const src = map.getSource('pwa-fleet-cluster-src') as mapboxgl.GeoJSONSource | undefined;
      if (src) { src.setData(fleetFC as any); return; }
      map.addSource('pwa-fleet-cluster-src', {
        type: 'geojson',
        data: fleetFC as any,
        cluster: true,
        clusterMaxZoom: LABEL_MIN_ZOOM,
        clusterRadius: CLUSTER_RADIUS,
      });
      map.addLayer({
        id: 'pwa-fleet-cluster-circles',
        type: 'circle',
        source: 'pwa-fleet-cluster-src',
        filter: ['has', 'point_count'],
        maxzoom: LABEL_MIN_ZOOM,
        paint: {
          'circle-color': '#ea580c',
          'circle-opacity': 0.85,
          'circle-radius': ['step', ['get', 'point_count'], 16, 5, 22, 15, 28],
          'circle-stroke-color': '#fff',
          'circle-stroke-width': 2,
        },
      });
      map.addLayer({
        id: 'pwa-fleet-cluster-count',
        type: 'symbol',
        source: 'pwa-fleet-cluster-src',
        filter: ['has', 'point_count'],
        maxzoom: LABEL_MIN_ZOOM,
        layout: {
          'text-field': '{point_count_abbreviated}',
          'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
          'text-size': 14,
        },
        paint: { 'text-color': '#fff' },
      });
      // Style unclustered (single-machine) points as a small cluster bubble too
      // so the zoomed-out view shows consistent orange-with-count circles
      // ("1", "2", "5"…) rather than tiny colored dots mixed with bigger bubbles.
      map.addLayer({
        id: 'pwa-fleet-cluster-points',
        type: 'circle',
        source: 'pwa-fleet-cluster-src',
        filter: ['!', ['has', 'point_count']],
        maxzoom: LABEL_MIN_ZOOM,
        paint: {
          'circle-color': '#ea580c',
          'circle-opacity': 0.85,
          'circle-radius': 16,
          'circle-stroke-color': '#fff',
          'circle-stroke-width': 2,
        },
      });
      // "1" text on top of every single-machine bubble — visual parity with the
      // multi-machine count labels.
      map.addLayer({
        id: 'pwa-fleet-cluster-points-count',
        type: 'symbol',
        source: 'pwa-fleet-cluster-src',
        filter: ['!', ['has', 'point_count']],
        maxzoom: LABEL_MIN_ZOOM,
        layout: {
          'text-field': '1',
          'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
          'text-size': 14,
        },
        paint: { 'text-color': '#fff' },
      });
      // Click cluster → zoom in to expand it
      map.on('click', 'pwa-fleet-cluster-circles', (e) => {
        const features = map.queryRenderedFeatures(e.point, { layers: ['pwa-fleet-cluster-circles'] });
        const clusterId = features[0]?.properties?.cluster_id;
        if (clusterId == null) return;
        const source = map.getSource('pwa-fleet-cluster-src') as any;
        source.getClusterExpansionZoom(clusterId, (err: any, zoom: number) => {
          if (err) return;
          map.easeTo({ center: (features[0].geometry as any).coordinates, zoom });
        });
      });
      map.on('mouseenter', 'pwa-fleet-cluster-circles', () => { map.getCanvas().style.cursor = 'pointer'; });
      map.on('mouseleave', 'pwa-fleet-cluster-circles', () => { map.getCanvas().style.cursor = ''; });
    };
    if (mapRef.current.isStyleLoaded()) setupCluster();
    else mapRef.current.once('idle', setupCluster);

    // ─── True-position layer: leader lines + exact-GPS dots ─────────────────
    // Only for machines whose marker got fanned out by spreadOverlaps. The line
    // points from the offset marker back to the real GPS dot. minzoom matches
    // LABEL_MIN_ZOOM so it only shows when HTML labels (not clusters) are active.
    const setupTruepos = () => {
      const map = mapRef.current!;
      // Compute fresh at the map's CURRENT zoom — not the construction-time zoom.
      // This runs on 'idle' (after the initial fitBounds settles), so the offset
      // is sane. Using a snapshot taken at zoom 9 flung the leader lines km out.
      // Exclude in-transit machines — shown via their trailer marker, so no
      // leader line / separate true-position dot for them.
      const fc = buildTrueposFC(spreadOverlaps(mapMachines.filter(m => m.transit_state !== 'in_transit'), map.getZoom()));
      const src = map.getSource('pwa-fleet-truepos-src') as mapboxgl.GeoJSONSource | undefined;
      if (src) { src.setData(fc as any); return; }
      map.addSource('pwa-fleet-truepos-src', { type: 'geojson', data: fc as any });
      map.addLayer({
        id: 'pwa-fleet-leader-lines', type: 'line', source: 'pwa-fleet-truepos-src',
        filter: ['==', ['get', 'kind'], 'line'], minzoom: LABEL_MIN_ZOOM,
        paint: { 'line-color': '#64748b', 'line-width': 1, 'line-dasharray': [2, 1.5], 'line-opacity': 0.85 },
      });
      map.addLayer({
        id: 'pwa-fleet-true-dots', type: 'circle', source: 'pwa-fleet-truepos-src',
        filter: ['==', ['get', 'kind'], 'dot'], minzoom: LABEL_MIN_ZOOM,
        paint: { 'circle-radius': 3.5, 'circle-color': '#334155', 'circle-stroke-color': '#fff', 'circle-stroke-width': 1.5 },
      });
    };
    if (mapRef.current.isStyleLoaded()) setupTruepos();
    else mapRef.current.once('idle', setupTruepos);

    // Sync HTML marker visibility to current zoom immediately
    updateLabelVisibility();

    // Job labels keyed by job_id and REUSED across reloads — recreating them on
    // every SSE/poll update made the labels flash as machines/trucks moved. A
    // geofence label never moves, so once placed it stays; we only add new ones
    // and remove jobs no longer shown.
    const liveJobIds = new Set<number>();
    for (const j of jobsToShow) {
      bounds.extend([j.lng, j.lat]);
      liveJobIds.add(j.job_id);
      if (jobMarkersRef.current.has(j.job_id)) continue; // already on the map — no flash

      const el = document.createElement('div');
      el.style.cssText = `display:flex;flex-direction:column;align-items:center;cursor:pointer;pointer-events:none;`;
      el.innerHTML = `
        <span class="fleet-marker-label" style="background:rgba(234,88,12,0.92);color:#fff;padding:2px 6px;border-radius:4px;font-size:11px;font-weight:700;white-space:nowrap;box-shadow:0 1px 3px rgba(0,0,0,0.4);text-transform:uppercase;letter-spacing:0.3px;">
          ${j.job_name}${j.bradanna_job_number ? ` <span style="opacity:0.8;font-weight:500">${j.bradanna_job_number}</span>` : ''}
        </span>
      `;
      // Anchor the label to the top edge of the geofence (north side) so it
      // sits ABOVE the circle instead of inside it where equipment markers pile up.
      // For polygons: use the northernmost vertex. For circles: lat + radius/111000.
      let labelLat = j.lat;
      let labelLng = j.lng;
      if (j.geofence_type === 'polygon' && Array.isArray(j.geofence_data?.points)) {
        for (const p of j.geofence_data.points) {
          if (Number(p.lat) > labelLat) { labelLat = Number(p.lat); labelLng = Number(p.lng); }
        }
      } else {
        const radius = Number(j.geofence_data?.radius) || 100;
        labelLat = j.lat + radius / 111_000;
      }
      const marker = new mapboxgl.Marker({ element: el, anchor: 'bottom', offset: [0, -6] }).setLngLat([labelLng, labelLat]).addTo(mapRef.current!);
      jobMarkersRef.current.set(j.job_id, marker);
    }
    // Remove labels for jobs no longer shown.
    for (const [id, marker] of jobMarkersRef.current.entries()) {
      if (!liveJobIds.has(id)) { marker.remove(); jobMarkersRef.current.delete(id); }
    }

    const features = jobsToShow.map((j) => {
      let coords: [number, number][] | null = null;
      if (j.geofence_type === 'polygon' && Array.isArray(j.geofence_data?.points)) {
        coords = j.geofence_data.points.map((p: any) => [Number(p.lng), Number(p.lat)] as [number, number]);
        if (coords && coords.length > 0 && (coords[0][0] !== coords[coords.length - 1][0] || coords[0][1] !== coords[coords.length - 1][1])) coords.push(coords[0]);
      } else {
        coords = circleToPolygon(j.lat, j.lng, Number(j.geofence_data?.radius) || 100, 48);
      }
      if (!coords || coords.length < 3) return null;
      return { type: 'Feature' as const, properties: { job_id: j.job_id, job_name: j.job_name }, geometry: { type: 'Polygon' as const, coordinates: [coords] } };
    }).filter(Boolean) as any[];

    const applyJobLayers = () => {
      const map = mapRef.current!;
      const src = map.getSource('fleet-job-geofences') as mapboxgl.GeoJSONSource | undefined;
      if (src) src.setData({ type: 'FeatureCollection', features });
      else {
        map.addSource('fleet-job-geofences', { type: 'geojson', data: { type: 'FeatureCollection', features } });
        map.addLayer({ id: 'fleet-job-geofence-fill', type: 'fill', source: 'fleet-job-geofences', paint: { 'fill-color': '#ea580c', 'fill-opacity': 0.12 } });
        map.addLayer({ id: 'fleet-job-geofence-line', type: 'line', source: 'fleet-job-geofences', paint: { 'line-color': '#ea580c', 'line-width': 1.5, 'line-opacity': 0.6 } });
      }
    };
    // Fall back on 'idle', NOT 'style.load': after a style reload (WebGL
    // context loss) this effect re-runs via styleEpoch, but by then
    // 'style.load' has already fired and never refires while isStyleLoaded()
    // is still false — a once('style.load') armed here never ran, so the
    // geofences stayed gone. 'idle' always fires again (same pattern as the
    // cluster/truepos layers above).
    if (mapRef.current!.isStyleLoaded()) applyJobLayers();
    else mapRef.current!.once('idle', applyJobLayers);

    // Only auto-position the camera on the FIRST data load. Subsequent SSE
    // position pushes update markers in place but must NOT move the camera.
    if (!didInitialFitRef.current) {
      if (!bounds.isEmpty()) mapRef.current.fitBounds(bounds, { padding: 60, maxZoom: 13 });
      didInitialFitRef.current = true;
    }
    setTimeout(updateLabelVisibility, 100);
  }, [data, mapMachines, activeJobs, jobsMode, styleEpoch, selectedType]);

  useEffect(() => { if (tab !== 'map') return; const id = setTimeout(() => mapRef.current?.resize(), 50); return () => clearTimeout(id); }, [tab]);

  // Re-spread HTML markers as the user zooms so pixel-based offset stays
  // consistent. Smoothly animates existing markers to new positions.
  // DOES NOT touch the cluster source — that always uses real lat/lng so
  // clusters group by actual location, not by spread positions (which would
  // throw markers across counties at low zoom).
  useEffect(() => {
    if (!mapRef.current || !data) return;
    const map = mapRef.current;
    const handler = () => {
      // Exclude in-transit machines (shown via trailer marker) so they get no
      // per-machine move, leader line, or true-position dot.
      const spread = spreadOverlaps(mapMachines.filter(m => m.transit_state !== 'in_transit'), map.getZoom());
      for (const m of spread) {
        const entry = markersByIdRef.current.get(m.equipment_id);
        // Zoom/pan only changes the pixel-based fan offset, not the true GPS —
        // snap labels into their new fanned position instantly. Without forceSnap
        // they slow-glide in over the live-fix duration (up to 12s), which reads
        // as labels "flying in" when you zoom into a stacked group.
        if (entry) animateMarker(entry, m.displayLng, m.displayLat, true);
      }
      // Leader lines / true dots depend on the (zoom-dependent) spread offset.
      const src = map.getSource('pwa-fleet-truepos-src') as mapboxgl.GeoJSONSource | undefined;
      if (src) src.setData(buildTrueposFC(spread) as any);
    };
    map.on('zoomend', handler);
    map.on('moveend', handler);
    // Recompute once the initial fitBounds settles so first-load offsets aren't
    // stuck at the construction zoom (which threw leader lines to infinity until
    // the user manually zoomed). 'idle' fires after the camera comes to rest.
    map.once('idle', handler);
    return () => { map.off('zoomend', handler); map.off('moveend', handler); };
  }, [data, mapMachines]);
  useEffect(() => { const onResize = () => mapRef.current?.resize(); window.addEventListener('resize', onResize); return () => window.removeEventListener('resize', onResize); }, []);
  useEffect(() => () => { mapRef.current?.remove(); mapRef.current = null; }, []);

  // Inject the ping keyframes once.
  useEffect(() => {
    if (document.getElementById('fleet-ping-style')) return;
    const s = document.createElement('style');
    s.id = 'fleet-ping-style';
    s.textContent = `@keyframes fleetPing { 0% { transform: scale(0.6); opacity: 0.85; } 70% { transform: scale(2.6); opacity: 0; } 100% { transform: scale(2.6); opacity: 0; } }`;
    document.head.appendChild(s);
  }, []);

  // Precise "exact position" ping: a pulsing dot placed at the highlighted
  // machine's REAL GPS coords (not the offset marker position).
  useEffect(() => {
    if (pingMarkerRef.current) { pingMarkerRef.current.remove(); pingMarkerRef.current = null; }
    if (!mapRef.current || highlightedId == null) return;
    const m = allMachines.find(x => x.equipment_id === highlightedId);
    if (!m || !Number.isFinite(m.lat) || !Number.isFinite(m.lng)) return;
    const el = document.createElement('div');
    el.style.cssText = 'position:relative;width:16px;height:16px;pointer-events:none;';
    el.innerHTML = `
      <span style="position:absolute;top:50%;left:50%;width:16px;height:16px;margin:-8px 0 0 -8px;border-radius:50%;background:rgba(234,88,12,0.45);animation:fleetPing 1.5s ease-out infinite;"></span>
      <span style="position:absolute;top:50%;left:50%;width:12px;height:12px;margin:-6px 0 0 -6px;border-radius:50%;background:#ea580c;border:2px solid #fff;box-shadow:0 0 4px rgba(0,0,0,0.5);"></span>
    `;
    pingMarkerRef.current = new mapboxgl.Marker({ element: el, anchor: 'center' })
      .setLngLat([m.lng, m.lat])
      .addTo(mapRef.current);
  }, [highlightedId, allMachines, tab]);

  // Arm/disarm follow-cam in lockstep with the selected machine. Selecting a
  // machine (openDrawer / flyToMachine sets highlightedId) starts following it;
  // closing the drawer (highlightedId → null) stops.
  useEffect(() => { followingIdRef.current = highlightedId; }, [highlightedId]);

  // The live TRUE GPS position of a machine — used to center the camera on
  // select/locate/follow. MUST be the real lat/lng, NOT the marker's
  // targetLng/targetLat (that's the SPREAD/display position; for a fanned shop
  // machine it sits far from the actual GPS, so centering on it flew the camera
  // hundreds of feet — or miles — away). Prefer the latest data position; fall
  // back to the marker only if the machine isn't in the current data set.
  const livePos = (equipmentId: number): [number, number] | null => {
    const m = allMachines.find(x => x.equipment_id === equipmentId);
    if (m && Number.isFinite(m.lng) && Number.isFinite(m.lat)) return [Number(m.lng), Number(m.lat)];
    const ent = markersByIdRef.current.get(equipmentId);
    if (ent && Number.isFinite(ent.targetLng) && Number.isFinite(ent.targetLat)) return [ent.targetLng, ent.targetLat];
    return null;
  };

  const openDrawer = async (equipmentId: number) => {
    setDrawerEquipmentId(equipmentId);
    setHighlightedId(equipmentId); // arms follow-cam via the sync effect
    // Center on the machine's LIVE position and zoom in if needed; follow-cam
    // keeps it framed as it moves.
    const pos = livePos(equipmentId);
    if (pos && mapRef.current) {
      mapRef.current.easeTo({ center: pos, zoom: Math.max(mapRef.current.getZoom(), 14), duration: 600 });
    }
    setActivity(null);
    setActivityLoading(true);
    try {
      const res = await fetchWithTimeout(`${API_BASE_URL}/api/equipment/${equipmentId}/recent-activity?days=7`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('bfa_token')}` },
      });
      const json = await res.json();
      setActivity(json.data);
    } catch (e: any) {
      setError(`Failed to load activity: ${e.message}`);
    } finally {
      setActivityLoading(false);
    }
  };
  const closeDrawer = () => { setDrawerEquipmentId(null); setHighlightedId(null); setActivity(null); };

  // Back button (header chevron / native hardware back) walks the in-page
  // history naturally: tab switches live in the URL (?tab=, pushed by setTab),
  // so back steps Unused → Types → Job → dashboard on its own. The equipment
  // drawer is the only non-URL layer, so it pushes ONE history entry while
  // open — back closes the drawer first, then resumes walking tabs. (The old
  // blanket fvGuard entry swallowed every back press and jumped straight to
  // the dashboard, which is why back never moved between tabs on Android.)
  const drawerOpen = drawerEquipmentId != null;
  useEffect(() => {
    if (!drawerOpen) return;
    window.history.pushState({ fvDrawer: true }, '');
    const onPop = () => { setDrawerEquipmentId(null); setHighlightedId(null); setActivity(null); };
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
      // Closed via the X button (not back): our entry is still on top — pop it
      // so the next back press doesn't need a dead click.
      if (window.history.state?.fvDrawer) window.history.back();
    };
  }, [drawerOpen]);

  const flyToJob = (group: FleetGroup) => {
    if (group.job_id === null) return;
    const geofence = activeJobs.find(j => j.job_id === group.job_id);
    const target = geofence
      ? { lng: geofence.lng, lat: geofence.lat }
      : group.equipment.length > 0
        ? { lng: group.equipment[0].lng, lat: group.equipment[0].lat }
        : null;
    if (!target) return;
    setTab('map');
    // Defer flyTo until after the map div is visible and resized.
    setTimeout(() => {
      mapRef.current?.resize();
      mapRef.current?.flyTo({ center: [target.lng, target.lat], zoom: 16, duration: 800 });
    }, 80);
  };

  // Fly the map to a single machine's own GPS position (not the job center)
  // and ping its exact location.
  const flyToMachine = (m: { equipment_id: number; lng: number | null; lat: number | null }) => {
    // Prefer the live marker position over the passed (poll-snapshot) coords.
    const pos = livePos(m.equipment_id) ?? (m.lng != null && m.lat != null ? [m.lng, m.lat] as [number, number] : null);
    if (!pos) return;
    setHighlightedId(m.equipment_id);
    setTab('map');
    setTimeout(() => {
      mapRef.current?.resize();
      mapRef.current?.flyTo({ center: pos, zoom: 15, duration: 800 });
    }, 80);
  };
  const drawerMachine = drawerEquipmentId != null ? allMachines.find(m => m.equipment_id === drawerEquipmentId) : null;

  if (!isAdmin) {
    return (
      <div style={{ padding: 24 }}>
        <p>Admin access required.</p>
        <button onClick={() => navigate('/')}>Home</button>
      </div>
    );
  }

  const totals = data?.totals;

  return (
    <div style={{ padding: '8px 16px 16px', maxWidth: 760, margin: '0 auto' }}>
      {/* Totals + refresh on one line (back button comes from global Header) */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10, fontSize: 13, flexWrap: 'wrap' as const }}>
        {totals ? (
          <>
            <span><strong style={{ fontSize: 17 }}>{totals.tracked_count}</strong> tracked</span>
            <span style={{ color: '#10b981' }}>· <strong style={{ fontSize: 17, color: '#10b981' }}>{totals.running_count}</strong> running</span>
            <span style={{ color: '#ef4444' }}>· <strong style={{ fontSize: 17, color: '#ef4444' }}>{totals.idle_count}</strong> unused</span>
          </>
        ) : <span style={{ color: '#94a3b8' }}>Loading…</span>}
        <button
          onClick={load}
          disabled={loading}
          aria-label="Refresh"
          style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'inline-flex' }}
        >
          <RefreshCw size={20} style={loading ? { animation: 'spin 1s linear infinite' } : {}} />
        </button>
        {/* Map view toggle — separated from the tab bar so we fit 4 list tabs on
            phone width. Highlighted when in map view; tapping a list tab returns. */}
        <button
          onClick={() => setTab(tab === 'map' ? 'list' : 'map')}
          aria-label={tab === 'map' ? 'Close map' : 'Open map'}
          title={tab === 'map' ? 'Close map' : 'Open map'}
          style={{
            background: tab === 'map' ? '#ea580c' : 'none',
            color: tab === 'map' ? '#fff' : '#0f172a',
            border: tab === 'map' ? 'none' : '1px solid #e5e7eb',
            cursor: 'pointer', padding: 6, display: 'inline-flex',
            borderRadius: 6, marginLeft: 4,
          }}
        >
          <MapIcon size={20} />
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', marginBottom: 10 }}>
        {([
          { key: 'list' as const, label: 'Job', icon: <ListIcon size={16} /> },
          { key: 'types' as const, label: 'Type', icon: <Layers size={16} /> },
          { key: 'rentals' as const, label: 'Rentals', icon: <Layers size={16} /> },
          { key: 'unused' as const, label: 'Unused', icon: <AlertTriangle size={16} /> },
        ]).map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              padding: '10px 14px', fontSize: 14,
              fontWeight: tab === t.key ? 700 : 500,
              color: tab === t.key ? '#ea580c' : '#64748b',
              background: 'none', border: 'none',
              borderBottom: tab === t.key ? '3px solid #ea580c' : '3px solid transparent',
              cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
            }}
          >
            {t.icon}{t.label}
          </button>
        ))}
      </div>

      {error && <div style={{ padding: 12, background: '#fee', borderRadius: 8, color: '#900', marginBottom: 12 }}>{error}</div>}
      {loading && !data && <div style={{ padding: 24, textAlign: 'center' as const, color: '#888' }}>Loading...</div>}

      {/* By Job */}
      <div style={{ display: tab === 'list' ? 'flex' : 'none', flexDirection: 'column' as const, gap: 16 }}>
        {data && data.groups.length === 0 && (
          <div style={{ padding: 32, textAlign: 'center' as const, color: '#888', background: '#f8fafc', borderRadius: 8 }}>
            No GPS-tracked equipment yet.
          </div>
        )}
        {/* In Transit — machines on the move, pulled out of "Off-site" so they
            read correctly (a machine on the road is in no geofence). */}
        {inTransitMachines.length > 0 && (
          <section style={{ background: '#fff', border: '1px solid #fde68a', borderRadius: 10, overflow: 'hidden' }}>
            <header style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: 'rgba(245,158,11,0.12)', borderBottom: '1px solid #f1f5f9' }}>
              <span style={{ fontSize: 16 }}>🚛</span>
              <strong style={{ fontSize: 19 }}>In Transit</strong>
              <span style={{ marginLeft: 'auto', fontSize: 12, color: '#b45309', fontWeight: 600, textTransform: 'uppercase' as const }}>
                {inTransitMachines.length} {inTransitMachines.length === 1 ? 'machine' : 'machines'}
              </span>
              <button
                onClick={() => {
                  setTab('map');
                  // After the map is visible, frame all in-transit machines.
                  setTimeout(() => {
                    if (!mapRef.current || inTransitMachines.length === 0) return;
                    mapRef.current.resize();
                    const b = new mapboxgl.LngLatBounds();
                    let any = false;
                    for (const m of inTransitMachines) {
                      const p = livePos(m.equipment_id);
                      if (p) { b.extend(p); any = true; }
                    }
                    if (any && !b.isEmpty()) mapRef.current.fitBounds(b, { padding: 70, maxZoom: 15 });
                  }, 120);
                }}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 10px', background: '#f59e0b', color: '#0f172a', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
              >
                <MapPin size={14} /> Map
              </button>
            </header>
            {(() => {
              // Hauler-anchored trailers lead with the TRUCK; everything else
              // (loose sticky groups, no-fix machines) renders flat as before.
              const haulerTrailers = listTrailers.filter(t => t.haulerId != null);
              const aboard = new Set(haulerTrailers.flatMap(t => t.machines.map(m => m.equipment_id)));
              const loose = inTransitMachines.filter(m => !aboard.has(m.equipment_id));
              const row = (m: FleetMachine) => (
                <MachineRow key={m.equipment_id} m={m} onClick={() => handleRowClick(m)} onLocate={() => flyToMachine(m)} onShowIssue={() => setIssuePopup(m)} />
              );
              return (
                <>
                  {haulerTrailers.map(t => (
                    <div key={t.trailer_id}>
                      <div
                        onClick={() => flyToMachine({ equipment_id: t.haulerId!, lat: t.lat, lng: t.lng })}
                        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', background: 'rgba(245,158,11,0.18)', borderTop: '1px solid #fde68a', cursor: 'pointer' }}
                      >
                        <span style={{ fontSize: 14 }}>🚛</span>
                        <strong style={{ fontSize: 14 }}>{t.haulerName || 'Truck'}</strong>
                        <span style={{ fontSize: 12, color: '#92400e', fontWeight: 600 }}>
                          hauling {t.machines.length} {t.machines.length === 1 ? 'machine' : 'machines'}
                        </span>
                        <span style={{ marginLeft: 'auto', background: '#0f172a', color: '#fff', padding: '2px 7px', borderRadius: 4, fontSize: 11, fontWeight: 700 }}>
                          {Math.round(t.speed_kph * 0.621371)} mph
                        </span>
                      </div>
                      {t.machines.map(row)}
                    </div>
                  ))}
                  {loose.map(row)}
                </>
              );
            })()}
          </section>
        )}
        {jobGroups.map(g => {
          const canFly = g.job_id !== null && (activeJobs.some(j => j.job_id === g.job_id) || g.equipment.length > 0);
          return (
            <section key={g.job_id ?? 'null'} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
              <header
                onClick={canFly ? () => flyToJob(g) : undefined}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px',
                  background: g.job_id === null ? 'rgba(239,68,68,0.08)' : 'rgba(234,88,12,0.08)',
                  borderBottom: '1px solid #f1f5f9',
                  cursor: canFly ? 'pointer' : 'default',
                }}
              >
                <MapPin size={16} style={{ color: g.job_id === null ? '#ef4444' : '#ea580c', flexShrink: 0, alignSelf: 'flex-start', marginTop: 3 }} />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <strong style={{ fontSize: 19 }}>{g.job_name}</strong>
                  {g.foreman_names && (
                    <div style={{ fontSize: 13, color: '#475569', fontWeight: 600, marginTop: 1 }}>
                      👷 {g.foreman_names}
                    </div>
                  )}
                </div>
                <span style={{ marginLeft: 'auto', alignSelf: 'flex-start', marginTop: 3, fontSize: 12, color: '#64748b', fontWeight: 600, textTransform: 'uppercase' as const, whiteSpace: 'nowrap' as const }}>
                  {g.equipment.length} {g.equipment.length === 1 ? 'machine' : 'machines'}
                </span>
              </header>
              {renderJobEquipment(g.equipment)}
            </section>
          );
        })}
      </div>

      {/* By Type — defaults to All Equipment grouped by type */}
      <div style={{ display: tab === 'types' ? 'flex' : 'none', flexDirection: 'column' as const, gap: 16 }}>
        {data && (() => {
          // Every type card keeps ALL its machines; within each card the rows
          // sub-group by subtype (Asphalt/Farm tagged, untagged under "Site") —
          // same treatment as By Job.
          const shownGroups = selectedType ? typeGroups.filter(g => g.type === selectedType) : typeGroups;
          const shownCount = shownGroups.reduce((s, g) => s + g.equipment.length, 0);
          return (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <label style={{ fontSize: 14, fontWeight: 600 }}>Type:</label>
                <select
                  value={selectedType}
                  onChange={e => setSelectedType(e.target.value)}
                  style={{ flex: 1, padding: '10px 12px', fontSize: 15, borderRadius: 8, border: '1px solid #d1d5db', background: '#fff' }}
                >
                  <option value="">All Equipment</option>
                  {equipmentTypes.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <span style={{ fontSize: 13, color: '#64748b' }}>{shownCount}</span>
                {selectedType && (
                  <button
                    onClick={() => {
                      setTab('map');
                      // Give the hidden→visible map a beat to resize, then frame
                      // just the selected type's machines.
                      setTimeout(() => {
                        const map = mapRef.current;
                        if (!map) return;
                        map.resize();
                        const b = new mapboxgl.LngLatBounds();
                        machinesOfSelectedType.forEach(m => { if (m.lat != null && m.lng != null) b.extend([m.lng, m.lat]); });
                        if (!b.isEmpty()) map.fitBounds(b, { padding: 70, maxZoom: 14 });
                      }, 120);
                    }}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '8px 12px', background: '#ea580c', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}
                  >
                    <MapPin size={14} /> Map
                  </button>
                )}
              </div>
              {shownCount === 0 && (
                <div style={{ padding: 32, textAlign: 'center' as const, color: '#888', background: '#f8fafc', borderRadius: 8 }}>
                  No GPS-tracked equipment yet.
                </div>
              )}
              {/* Asphalt — pinned cross-type card at the top, only on All Equipment.
                  When a type is selected, asphalt machines of that type still show
                  under the card's Asphalt sub-header. */}
              {!selectedType && (() => {
                const meta = SUBTYPES.find(s => s.value === 'asphalt')!;
                const asphalt = allMachines.filter(m => (m.equipment_subtype || '') === meta.value);
                if (asphalt.length === 0) return null;
                // Machines parked at the Shop (job 1) or Farm (job 2) sink to the
                // bottom so the ones actually out on jobs read first.
                const atHome = (m: FlatMachine) => m._job_id === 1 || m._job_id === 2;
                const ordered = [...asphalt].sort((a, b) => {
                  const d = (atHome(a) ? 1 : 0) - (atHome(b) ? 1 : 0);
                  return d !== 0 ? d : (a.equipment_name || '').localeCompare(b.equipment_name || '');
                });
                return (
                  <section style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
                    <header style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: 'rgba(59,130,246,0.08)', borderBottom: '1px solid #f1f5f9' }}>
                      <Layers size={16} style={{ color: '#3b82f6' }} />
                      <strong style={{ fontSize: 15 }}>Asphalt Equipment</strong>
                      <span style={{ marginLeft: 'auto', fontSize: 12, color: '#64748b', fontWeight: 600, textTransform: 'uppercase' as const }}>
                        {ordered.length} {ordered.length === 1 ? 'machine' : 'machines'}
                      </span>
                    </header>
                    {ordered.map(m => machineRow(m, true))}
                  </section>
                );
              })()}
              {shownGroups.map(g => (
                <section key={g.type} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
                  <header style={{
                    display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px',
                    background: 'rgba(59,130,246,0.08)', borderBottom: '1px solid #f1f5f9',
                  }}>
                    <Layers size={16} style={{ color: '#3b82f6' }} />
                    <strong style={{ fontSize: 15 }}>{displayType(g.type)}</strong>
                    <span style={{ marginLeft: 'auto', fontSize: 12, color: '#64748b', fontWeight: 600, textTransform: 'uppercase' as const }}>
                      {g.equipment.length} {g.equipment.length === 1 ? 'machine' : 'machines'}
                    </span>
                  </header>
                  {renderTypeEquipment(g.equipment)}
                </section>
              ))}
            </>
          );
        })()}
      </div>

      {/* Rentals */}
      <div style={{ display: tab === 'rentals' ? 'block' : 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <button
            onClick={openAddRental}
            style={{ padding: '10px 16px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}
          >
            + Add Rental
          </button>
          <span style={{ marginLeft: 'auto', fontSize: 13, color: '#64748b' }}>
            {rentals.length} active
          </span>
        </div>
        {rentalsLoading && <div style={{ padding: 16, color: '#64748b' }}>Loading…</div>}
        {!rentalsLoading && rentals.length === 0 && (
          <div style={{ padding: 32, textAlign: 'center', color: '#888', background: '#f8fafc', borderRadius: 8 }}>
            No active rentals. Tap <strong>Add Rental</strong> to register one.
          </div>
        )}
        {rentals.length > 0 && (
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
            {rentals.map((r, i) => (
              <div key={r.rental_id} style={{
                padding: '12px 14px', borderTop: i === 0 ? 'none' : '1px solid #f1f5f9',
                display: 'flex', flexDirection: 'column' as const, gap: 6,
              }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' as const }}>
                  <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#3b82f6', flexShrink: 0 }} />
                  <strong style={{ fontSize: 15 }}>{r.description || r.equipment_type}</strong>
                  <span style={{ fontSize: 12, color: '#64748b' }}>{r.equipment_type}</span>
                  <span style={{ marginLeft: 'auto', padding: '2px 8px', borderRadius: 4, background: 'rgba(59,130,246,0.15)', color: '#1e40af', fontSize: 11, fontWeight: 700 }}>
                    {r.rental_company}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: '#64748b' }}>
                  📍 {r.job_name || 'Unassigned'} · {r.days_on_rent}d on rent
                </div>
                <select
                  value={r.job_id ?? ''}
                  onChange={e => moveRental(r, e.target.value ? Number(e.target.value) : null)}
                  style={{ padding: '8px 10px', fontSize: 14, borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', width: '100%' }}
                >
                  <option value="">— Unassigned —</option>
                  {activeJobs.map(j => (
                    <option key={j.job_id} value={j.job_id}>
                      {j.job_name}{j.bradanna_job_number ? ` (${j.bradanna_job_number})` : ''}
                    </option>
                  ))}
                </select>
                <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                  <button onClick={() => openEditRental(r)} style={{ flex: 1, padding: '8px 10px', background: '#e2e8f0', color: '#0f172a', border: 'none', borderRadius: 6, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>Edit</button>
                  <button onClick={() => returnRental(r)} style={{ flex: 1, padding: '8px 10px', background: '#10b981', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>Return</button>
                  {auth?.user?.is_super_admin && (
                    <button onClick={() => deleteRental(r)} style={{ padding: '8px 10px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>Delete</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Focused move-rental modal — opened by tapping a rental row in a list tab. */}
      {movingRental && (
        <div
          onClick={() => !moveSaving && setMovingRental(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 16 }}
        >
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, padding: 18, maxWidth: 420, width: '100%' }}>
            <h2 style={{ margin: '0 0 4px', fontSize: 18 }}>Move Rental</h2>
            <div style={{ fontSize: 13, color: '#64748b', marginBottom: 12 }}>
              <strong style={{ color: '#0f172a' }}>{movingRental.description || movingRental.equipment_type}</strong>
              <span style={{ marginLeft: 6 }}>· {movingRental.rental_company}</span>
            </div>
            <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>
              Currently at: <strong style={{ color: '#0f172a' }}>{movingRental.job_name || '— Unassigned —'}</strong>
            </div>
            <label style={{ display: 'flex', flexDirection: 'column' as const, gap: 6, fontSize: 13, fontWeight: 600, marginTop: 8 }}>
              Move to job
              <select
                value={moveTargetJobId}
                onChange={e => setMoveTargetJobId(e.target.value)}
                autoFocus
                style={{ padding: 10, fontSize: 14, border: '1px solid #d1d5db', borderRadius: 6, background: '#fff' }}
              >
                <option value="">— Unassigned —</option>
                {activeJobs.map(j => (
                  <option key={j.job_id} value={j.job_id}>
                    {j.job_name}{j.bradanna_job_number ? ` (${j.bradanna_job_number})` : ''}
                  </option>
                ))}
              </select>
            </label>
            <div style={{ display: 'flex', gap: 8, marginTop: 16, alignItems: 'center' }}>
              <button
                onClick={async () => {
                  if (!movingRental) return;
                  if (!confirm(`Mark "${movingRental.description || movingRental.equipment_type}" (${movingRental.rental_company}) as returned?`)) return;
                  setMoveSaving(true);
                  try {
                    await apiCall(`/rentals/${movingRental.rental_id}/return`, 'POST');
                    setMovingRental(null);
                    await loadRentals();
                    await load();
                  } catch (e: any) {
                    alert(`Failed: ${e.message}`);
                  } finally {
                    setMoveSaving(false);
                  }
                }}
                disabled={moveSaving}
                style={{ padding: '10px 16px', background: '#10b981', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600, cursor: 'pointer' }}
              >
                Return
              </button>
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                <button onClick={() => setMovingRental(null)} disabled={moveSaving} style={{ padding: '10px 16px', background: '#e2e8f0', color: '#0f172a', border: 'none', borderRadius: 6, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
                <button onClick={submitMoveRental} disabled={moveSaving} style={{ padding: '10px 16px', background: moveSaving ? '#94a3b8' : '#3b82f6', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600, cursor: moveSaving ? 'not-allowed' : 'pointer' }}>
                  {moveSaving ? 'Moving…' : 'Move'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rental add/edit modal */}
      {showRentalModal && (
        <div
          onClick={() => !rentalSaving && setShowRentalModal(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 16 }}
        >
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, padding: 18, maxWidth: 440, width: '100%', maxHeight: '90vh', overflow: 'auto' }}>
            <h2 style={{ margin: '0 0 16px', fontSize: 18 }}>
              {editingRental ? 'Edit Rental' : 'Add Rental'}
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 10 }}>
              <label style={{ display: 'flex', flexDirection: 'column' as const, gap: 4, fontSize: 13, fontWeight: 600 }}>
                Equipment type *
                <select
                  value={rentalForm.equipment_type}
                  onChange={e => setRentalForm(f => ({ ...f, equipment_type: e.target.value }))}
                  style={{ padding: 10, fontSize: 14, border: '1px solid #d1d5db', borderRadius: 6, background: '#fff' }}
                >
                  <option value="">— Select type —</option>
                  {Object.keys(TYPE_COLORS).sort().map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </label>
              <label style={{ display: 'flex', flexDirection: 'column' as const, gap: 4, fontSize: 13, fontWeight: 600 }}>
                Description
                <input type="text" value={rentalForm.description} onChange={e => setRentalForm(f => ({ ...f, description: e.target.value }))} placeholder="Cat 320 long-reach" style={{ padding: 10, fontSize: 14, border: '1px solid #d1d5db', borderRadius: 6 }} />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column' as const, gap: 4, fontSize: 13, fontWeight: 600 }}>
                Rental company *
                <select
                  value={companyMode === 'other' ? '__other__' : rentalForm.rental_company}
                  onChange={e => {
                    if (e.target.value === '__other__') {
                      setCompanyMode('other');
                      setRentalForm(f => ({ ...f, rental_company: '' }));
                    } else {
                      setCompanyMode('select');
                      setRentalForm(f => ({ ...f, rental_company: e.target.value }));
                    }
                  }}
                  style={{ padding: 10, fontSize: 14, border: '1px solid #d1d5db', borderRadius: 6, background: '#fff' }}
                >
                  <option value="">— Select vendor —</option>
                  {RENTAL_COMPANIES.map(c => <option key={c} value={c}>{c}</option>)}
                  <option value="__other__">Other (type below)…</option>
                </select>
                {companyMode === 'other' && (
                  <input
                    type="text"
                    value={rentalForm.rental_company}
                    onChange={e => setRentalForm(f => ({ ...f, rental_company: e.target.value }))}
                    placeholder="Type vendor name"
                    autoFocus
                    style={{ padding: 10, fontSize: 14, border: '1px solid #d1d5db', borderRadius: 6, marginTop: 6 }}
                  />
                )}
              </label>
              <label style={{ display: 'flex', flexDirection: 'column' as const, gap: 4, fontSize: 13, fontWeight: 600 }}>
                Vendor unit # (optional)
                <input type="text" value={rentalForm.vendor_equipment_id} onChange={e => setRentalForm(f => ({ ...f, vendor_equipment_id: e.target.value }))} style={{ padding: 10, fontSize: 14, border: '1px solid #d1d5db', borderRadius: 6 }} />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column' as const, gap: 4, fontSize: 13, fontWeight: 600 }}>
                Current job
                <select value={rentalForm.job_id} onChange={e => setRentalForm(f => ({ ...f, job_id: e.target.value }))} style={{ padding: 10, fontSize: 14, border: '1px solid #d1d5db', borderRadius: 6, background: '#fff' }}>
                  <option value="">— Unassigned —</option>
                  {activeJobs.map(j => (
                    <option key={j.job_id} value={j.job_id}>
                      {j.job_name}{j.bradanna_job_number ? ` (${j.bradanna_job_number})` : ''}
                    </option>
                  ))}
                </select>
              </label>
              <label style={{ display: 'flex', flexDirection: 'column' as const, gap: 4, fontSize: 13, fontWeight: 600 }}>
                Notes
                <textarea value={rentalForm.notes} onChange={e => setRentalForm(f => ({ ...f, notes: e.target.value }))} rows={3} style={{ padding: 10, fontSize: 14, border: '1px solid #d1d5db', borderRadius: 6, fontFamily: 'inherit' }} />
              </label>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowRentalModal(false)} disabled={rentalSaving} style={{ padding: '10px 16px', background: '#e2e8f0', color: '#0f172a', border: 'none', borderRadius: 6, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
              <button onClick={saveRental} disabled={rentalSaving} style={{ padding: '10px 16px', background: rentalSaving ? '#94a3b8' : '#3b82f6', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600, cursor: rentalSaving ? 'not-allowed' : 'pointer' }}>
                {rentalSaving ? 'Saving…' : editingRental ? 'Save' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unused */}
      <div style={{ display: tab === 'unused' ? 'block' : 'none' }}>
        {data && (unusedMachines.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center' as const, color: '#888', background: '#f8fafc', borderRadius: 8 }}>
            No unused equipment — every tracked machine has run in the last 2 days. 🎉
          </div>
        ) : (
          <section style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
            <header style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px',
              background: 'rgba(239,68,68,0.08)', borderBottom: '1px solid #f1f5f9',
            }}>
              <AlertTriangle size={16} style={{ color: '#ef4444' }} />
              <strong style={{ fontSize: 15 }}>Unused equipment</strong>
              <span style={{ fontSize: 12, color: '#64748b' }}>(no engine hours 2+ days)</span>
              <span style={{ marginLeft: 'auto', fontSize: 12, color: '#64748b', fontWeight: 600, textTransform: 'uppercase' as const }}>
                {unusedMachines.length} {unusedMachines.length === 1 ? 'machine' : 'machines'}
              </span>
            </header>
            {unusedMachines.map(m => <MachineRow key={m.equipment_id} m={m} onClick={() => openDrawer(m.equipment_id)} showLocation onLocate={() => flyToMachine(m)} onShowIssue={() => setIssuePopup(m)} />)}
          </section>
        ))}
      </div>

      {/* Map (always mounted, display-toggled). Height fits viewport so the page itself doesn't scroll. */}
      <div
        style={{
          display: tab === 'map' ? 'block' : 'none',
          position: 'relative' as const,
          height: 'calc(100dvh - 252px - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))',
          minHeight: 280,
          borderRadius: 8,
          overflow: 'hidden',
          border: '1px solid #e5e7eb',
        }}
      >
        <div ref={mapContainer} style={{ width: '100%', height: '100%' }} />
        {resuming && (
          <div style={{ position: 'absolute', top: 8, left: '50%', transform: 'translateX(-50%)', zIndex: 5, background: 'rgba(15,23,42,0.9)', color: '#fff', padding: '5px 12px', borderRadius: 999, fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6, pointerEvents: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.3)' }}>
            <span style={{ width: 10, height: 10, border: '2px solid #fff', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.8s linear infinite' }} />
            Updating live positions…
          </div>
        )}

        {/* Equipment-type legend removed — not needed. */}

        {/* Top-left: type filter — change it right on the map. Shares
            selectedType with the By Type tab so the two views stay in sync. */}
        <div style={{
          position: 'absolute' as const, top: 8, left: 8, zIndex: 5,
          display: 'flex', alignItems: 'center', gap: 6,
          background: 'rgba(15,23,42,0.92)', color: '#fff',
          padding: '5px 7px', borderRadius: 8,
          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
        }}>
          <select
            value={selectedType}
            onChange={e => setSelectedType(e.target.value)}
            style={{
              background: 'rgba(255,255,255,0.08)', color: '#fff',
              border: '1px solid rgba(255,255,255,0.25)', borderRadius: 5,
              padding: '4px 4px', fontSize: 12, fontWeight: 600, maxWidth: 150,
            }}
          >
            <option value="" style={{ color: '#0f172a' }}>All Equipment</option>
            {equipmentTypes.map(t => <option key={t} value={t} style={{ color: '#0f172a' }}>{t}</option>)}
          </select>
          {selectedType && (
            <>
              <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.75)', fontWeight: 600 }}>{machinesOfSelectedType.length}</span>
              <button
                onClick={() => setSelectedType('')}
                title="Show all equipment"
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: 4,
                  color: '#fff', cursor: 'pointer', padding: 3,
                }}
              >
                <X size={14} />
              </button>
            </>
          )}
        </div>

        {/* Bottom-right: jobs toggle */}
        <button
          onClick={() => setJobsMode(m => m === 'active' ? 'all' : m === 'all' ? 'none' : 'active')}
          style={{
            position: 'absolute' as const, bottom: 8, right: 8, zIndex: 5,
            padding: '5px 10px', border: 'none', borderRadius: 16,
            background: jobsMode === 'none' ? 'rgba(15,23,42,0.92)' : 'rgba(234,88,12,0.92)',
            color: '#fff', fontSize: 11, fontWeight: 600, cursor: 'pointer',
            whiteSpace: 'nowrap' as const, boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
          }}
        >
          {jobsMode === 'active' && '⬤ Active jobs'}
          {jobsMode === 'all' && '⬤ All jobs'}
          {jobsMode === 'none' && '○ Jobs off'}
        </button>
      </div>

      {/* Logged-issue popup */}
      {issuePopup && issuePopup.open_issue && (
        <div onClick={() => setIssuePopup(null)} style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.55)',
          zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, maxWidth: 440, width: '100%', overflow: 'hidden', boxShadow: '0 10px 40px rgba(0,0,0,0.3)' }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '16px 20px',
              background: issuePopup.open_issue.severity === 'critical' ? '#dc2626' : '#ea580c', color: '#fff',
            }}>
              <AlertTriangle size={22} />
              <strong style={{ fontSize: 17, flex: 1 }}>
                {issuePopup.open_issue.severity === 'critical' ? 'Critical' : 'Moderate'} issue
              </strong>
              <button onClick={() => setIssuePopup(null)} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer' }}><X size={24} /></button>
            </div>
            <div style={{ padding: 20 }}>
              <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>
                {displayName(issuePopup.equipment_name)}
                {issuePopup.bradanna_equipment_number && <span style={{ marginLeft: 8, color: '#64748b', fontWeight: 500, fontSize: 14 }}>{issuePopup.bradanna_equipment_number}</span>}
              </div>
              <p style={{ margin: '12px 0 0', fontSize: 15, lineHeight: 1.5, color: '#0f172a', whiteSpace: 'pre-wrap' as const }}>
                {issuePopup.open_issue.issue_description || 'No description provided.'}
              </p>
              {issuePopup.open_issue.reported_date && (
                <div style={{ marginTop: 14, fontSize: 13, color: '#64748b' }}>
                  Reported {new Date(issuePopup.open_issue.reported_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Activity drawer */}
      {drawerEquipmentId != null && drawerMachine && (
        <Portal>
        <div onClick={closeDrawer} style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.55)',
          zIndex: 1000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: '#fff', borderTopLeftRadius: 16, borderTopRightRadius: 16,
            width: '100%', maxWidth: 760, maxHeight: '85vh', overflowY: 'auto' as const, padding: 20,
            paddingBottom: 'calc(20px + env(safe-area-inset-bottom, 0px))',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <span style={{ width: 14, height: 14, borderRadius: '50%', background: machineColor(drawerMachine), flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 18, fontWeight: 700 }}>
                  {displayName(drawerMachine.equipment_name)}
                  {drawerMachine.bradanna_equipment_number && <span style={{ marginLeft: 8, color: '#64748b', fontWeight: 500, fontSize: 14 }}>{drawerMachine.bradanna_equipment_number}</span>}
                </div>
                <div style={{ fontSize: 13, color: '#64748b' }}>{displayType(drawerMachine.equipment_type)}</div>
              </div>
              <button onClick={closeDrawer} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={26} /></button>
            </div>

            <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
              <div style={{ flex: 1, padding: 10, background: '#f8fafc', borderRadius: 10, textAlign: 'center' as const }}>
                <div style={{ fontSize: 20, fontWeight: 700 }}>{drawerMachine.current_hours != null ? Math.round(drawerMachine.current_hours) : '—'}</div>
                <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase' as const }}>Total Hrs</div>
              </div>
              <div style={{ flex: 1, padding: 10, background: '#f8fafc', borderRadius: 10, textAlign: 'center' as const }}>
                <div style={{ fontSize: 20, fontWeight: 700 }}>{activity ? formatHrs(activity.total_minutes_running) : '—'}</div>
                <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase' as const }}>Run 7d</div>
              </div>
              <div style={{ flex: 1, padding: 10, background: '#f8fafc', borderRadius: 10, textAlign: 'center' as const }}>
                <div style={{ fontSize: 20, fontWeight: 700 }}>{activity?.days_with_runtime ?? '—'}</div>
                <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase' as const }}>Days run</div>
              </div>
            </div>

            <h3 style={{ margin: '0 0 8px', fontSize: 15 }}>Last 7 days</h3>
            {activityLoading && <div style={{ padding: 24, textAlign: 'center' as const, color: '#888' }}>Loading...</div>}
            {activity && (activity.by_day.every(d => d.total_minutes === 0) ? (
              <div style={{ padding: 16, color: '#94a3b8', textAlign: 'center' as const, background: '#f8fafc', borderRadius: 8 }}>
                No engine hours in last {activity.days} days
              </div>
            ) : (
              activity.by_day.map(d => (
                <div key={d.date} style={{ marginBottom: 8, padding: 10, background: d.total_minutes > 0 ? '#fff' : '#fafafa', border: '1px solid #e5e7eb', borderRadius: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: d.jobs.length > 0 ? 6 : 0 }}>
                    <strong style={{ fontSize: 14 }}>{new Date(d.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</strong>
                    <span style={{ marginLeft: 'auto', fontSize: 14, fontWeight: 600, color: d.total_minutes > 0 ? '#0f172a' : '#94a3b8' }}>{d.total_minutes > 0 ? formatHrs(d.total_minutes) : '—'}</span>
                  </div>
                  {d.jobs.map(j => (
                    <div key={String(j.job_id)} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#475569', paddingLeft: 4 }}>
                      <MapPin size={12} />
                      <span style={{ flex: 1 }}>{j.job_name}{j.bradanna_job_number ? ` (${j.bradanna_job_number})` : ''}</span>
                      <span style={{ fontWeight: 600 }}>{formatHrs(j.minutes)}</span>
                    </div>
                  ))}
                </div>
              ))
            ))}

            <button
              onClick={() => { closeDrawer(); navigate(`/mechanics/machine-hub?equipment=${drawerEquipmentId}`); }}
              style={{ marginTop: 14, width: '100%', padding: 14, background: '#0f172a', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 16, fontWeight: 600, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
            >
              <ExternalLink size={18} /> Open in Machine Hub
            </button>
          </div>
        </div>
        </Portal>
      )}
    </div>
  );
};

const MachineRow: React.FC<{ m: FleetMachine & { _job_name?: string; _job_number?: string | null; _job_id?: number | null }; onClick: () => void; showLocation?: boolean; onLocate?: () => void; onShowIssue?: () => void }> = ({ m, onClick, showLocation, onLocate, onShowIssue }) => {
  // Status badges (rental / unused / issue / service). Reused either inline
  // under the name (By Job) or on the bottom row (By Type) — the type view leads
  // with a big name + location, so the pills move out of the way to the bottom.
  const badges = (
    <>
      {m.is_rental && (
        <span style={{ padding: '1px 6px', borderRadius: 4, background: 'rgba(59,130,246,0.15)', color: '#1e40af', fontSize: 10, fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: 0.4 }}>
          🏷 Rental · {m.rental_company}
        </span>
      )}
      {!m.is_rental && m.open_issue && (
        <span
          onClick={(e) => { e.stopPropagation(); onShowIssue?.(); }}
          title="View logged issue"
          style={{ ...fleetBadge, marginLeft: 0, cursor: 'pointer',
            background: m.open_issue.severity === 'critical' ? '#dc2626' : '#ea580c',
            color: '#fff',
            boxShadow: m.open_issue.severity === 'critical' ? '0 0 0 2px rgba(220,38,38,0.25)' : undefined }}
        >
          {m.open_issue.severity === 'critical' ? 'Critical' : 'Moderate'}
        </span>
      )}
      {!m.is_rental && serviceDue(m) && (
        <span style={{ ...fleetBadge, marginLeft: 0, background: '#2563eb', color: '#fff' }}>
          SERVICE {sinceService(m)!.toLocaleString()}h
        </span>
      )}
      {/* No service baseline at all (used machine, never inspected): say so in
          gray rather than showing nothing, which reads as "fine". Clears on the
          machine's first completed inspection. */}
      {!m.is_rental && m.service_baseline === 'none' && (
        <span title="No inspection or service on record — needs a first inspection to start the service clock"
          style={{ ...fleetBadge, marginLeft: 0, background: '#64748b', color: '#fff' }}>
          NO RECORD
        </span>
      )}
      {!m.is_rental && (() => {
        const tb = trackerBadge(m);
        return tb ? (
          <span title="Tracker health" style={{ ...fleetBadge, marginLeft: 0, background: tb.color, color: '#fff' }}>
            📡 {tb.text}
          </span>
        ) : null;
      })()}
    </>
  );
  const hasBadges = m.is_rental || !!m.open_issue || serviceDue(m) || m.service_baseline === 'none' || !!trackerBadge(m);
  return (
  <div
    onClick={onClick}
    style={{
      padding: '12px 14px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer',
      display: 'flex', alignItems: 'center', gap: 12,
      background: m.is_rental ? 'rgba(59,130,246,0.05)' : undefined,
    }}
  >
    <span style={{ width: 12, height: 12, borderRadius: '50%', background: m.is_rental ? '#3b82f6' : machineColor(m), flexShrink: 0 }} />
    <div style={{ flex: 1, minWidth: 0 }}>
      {/* Line 1: model-first name + type ("255 Cat Skid Steer"). Equipment
          number is intentionally NOT shown in list rows (declutter) — it lives
          in the detail drawer / issue popup. */}
      <div style={{ fontSize: 15, fontWeight: 600 }}>
        {displayName(m.equipment_name)}
        <span style={{ marginLeft: 6, fontWeight: 500 }}>{displayType(m.equipment_type)}</span>
      </div>
      {showLocation ? (
        /* By Type: location line under the name. Muted slate, not orange —
           alternating black/orange rows down the list was hard on the eyes. */
        <div style={{ fontSize: 13, color: '#475569', fontWeight: 500, marginTop: 2, marginLeft: 3, display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
          <MapPin size={13} style={{ flexShrink: 0 }} />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>
            {m._job_name || 'No job'}
          </span>
          {m.is_rental && m.days_on_rent != null && (
            <span style={{ fontSize: 12, color: '#64748b', flexShrink: 0 }}>· {m.days_on_rent}d on rent</span>
          )}
        </div>
      ) : (
        /* By Job: status badges only. */
        hasBadges && (
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 2, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' as const }}>
            {badges}
            {m.is_rental && m.days_on_rent != null && <span>{m.days_on_rent}d on rent</span>}
          </div>
        )
      )}
      {/* By Type: status pills on their own left-aligned line so they line up
          down the column and the flags are easy to scan at a glance. */}
      {showLocation && hasBadges && (
        <div style={{ marginTop: 5, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' as const }}>
          {badges}
        </div>
      )}
    </div>
    {/* Hours run today, right-aligned before the locate button. Green = ran.
        (Run state itself is the row dot — green while running — so no
        separate "Running now"/"Off" text line.) Idle machines never ran
        today, so their day count borrows this slot as small muted text
        instead of a badge pill — keeps the rows quiet. */}
    {!m.is_rental && (m.ran_today_minutes > 0 ? (
      <span style={{ fontSize: 13, fontWeight: 700, color: '#10b981', whiteSpace: 'nowrap' as const, flexShrink: 0 }}>
        {formatHrs(m.ran_today_minutes)}
      </span>
    ) : m.is_idle ? (
      <span style={{ fontSize: 12, fontWeight: 700, color: '#94a3b8', whiteSpace: 'nowrap' as const, flexShrink: 0 }}>
        {unusedLabel(m)} idle
      </span>
    ) : null)}
    {onLocate && m.lat != null && m.lng != null && (
      <button
        onClick={(e) => { e.stopPropagation(); onLocate(); }}
        title="Show this machine on the map"
        style={{ display: 'inline-flex', padding: 6, borderRadius: 6, border: 'none', background: 'transparent', cursor: 'pointer', flexShrink: 0 }}
      >
        <Crosshair size={16} style={{ color: '#ea580c' }} />
      </button>
    )}
  </div>
  );
};

export default FleetView;
